import {
	Badge,
	Box,
	Button,
	Icons,
	CheckboxInput,
	useId,
	Inline,
	Stack,
	Heading,
	Text,
	joinAttributes,
	Spinner,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	ShadowRoot,
} from "@sembark-travel/ui-base"
import { useDialog, Dialog } from "@sembark-travel/ui-dialog"
import {
	CopyToClipboardButton,
	CopyToClipboard,
} from "@sembark-travel/ui-copy-to-clipboard"
import { ShareViaWhatsApp } from "@sembark-travel/ui-share"
import { SendOperationalBookingsOnWhatsApp } from "./SendOnWhatsApp"
import {
	utcTimestampToLocalDate,
	utcTimestampToLocalDateString,
	getDiff,
	localOrUtcTimestampToLocalDate,
	formatDate,
	parseDate,
} from "@sembark-travel/datetime-utils"
import { withOrdinalSuffix } from "@sembark-travel/number-utils"
import { useXHR } from "@sembark-travel/xhr"
import {
	arrayMutators,
	CheckboxInputField,
	Form,
	GetFieldValue,
	isTruthy,
} from "@sembark-travel/ui-form"
import pluralize from "pluralize"
import React, {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useState,
} from "react"
import useSWR from "swr"
import { Optional, Required } from "utility-types"
import { ICab } from "../Cabs"
import { ICabType } from "../CabTypes"
import { TDriver } from "../Drivers"
import type { ISupplier } from "../Supplier"
import { collect } from "../utils"
import type { IScheduledCab, ICabSchedule } from "./../CabScheduling/store"
import type { TArrivalDepartureDetail } from "../Trips"
import { combineBookings, TTripOperationalBooking } from "./store"
import { TTravelActiviytBooking } from "../TravelActivityBookings"
import {
	TMoney,
	addMoneyFromDifferentCurrencies,
	formatMoneyByIntl,
	isZeroMoney,
	moneyParseByDecimal,
} from "@sembark-travel/money"
import { Markdown } from "@sembark-travel/ui-markdown"
import { getStartDateTimeOfCabSchedule } from "../CabScheduling"
import { useSynedStorageState } from "../storage"
import { getSameCabInfo } from "./utils"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { IHotel } from "../Hotels"
import {
	formatTouristInfoForWhatsapp,
	TouristInfoEmailView,
	TTourist,
} from "../Tourists"
import { TTransportServiceLocationPoint } from "../TransportServices"
import { Link } from "@sembark-travel/ui-router"
import { generatePath } from "../router-utils"
import { PhoneNumber } from "../Contacts"

type TSharableTripOperationalBooking = Optional<
	Pick<TTripOperationalBooking, "id" | "daywise_services">,
	"daywise_services"
>

const TrackingShareContentType = "cab_schedules"

type ShareWith = Array<
	"guest" | "transportServiceProvider" | "driver" | "booking"
>

const DEFAULT_SHARE_WITH: ShareWith = [
	"guest",
	"transportServiceProvider",
	"driver",
	"booking",
]

type TShareTripOperationalBookingsProps = {
	trip?: TSharableTripOperationalBooking
	trips?: Array<TSharableTripOperationalBooking>
	date?: Date
	canShareWith?: ShareWith
	cabSchedules?: Array<ICabSchedule>
	travelActivityBookings?: Array<TTravelActiviytBooking>
	children: (props: { share: () => void }) => React.ReactNode
}

export function ShareTripOperationalBookings({
	trip,
	trips,
	children,
	cabSchedules,
	travelActivityBookings,
	canShareWith = DEFAULT_SHARE_WITH,
}: TShareTripOperationalBookingsProps) {
	const [isOpen, open, close] = useDialog()
	const [{ selectedTripOperationalBookings, selectedTripId }, dispatch] =
		useReducer<
			React.Reducer<
				{
					selectedTripOperationalBookings: Array<TSharableTripOperationalBooking>
					selectedTripId?: number
				},
				{
					selectedTripOperationalBookings?: Array<TSharableTripOperationalBooking>
					selectedTripId?: number
				}
			>
		>((state, action) => ({ ...state, ...action }), {
			selectedTripOperationalBookings: trip ? [trip] : trips || [],
			selectedTripId: undefined,
		})
	const onCancel = useCallback(() => {
		close()
		dispatch({
			selectedTripOperationalBookings: trip ? [trip] : trips || [],
			selectedTripId: undefined,
		})
	}, [close, trip, trips])
	return (
		<>
			{children({
				share: () => {
					open()
				},
			})}
			{isOpen ? (
				<Dialog open={isOpen} onClose={onCancel} xl>
					<Dialog.Header>
						<Box display={{ sm: "flex" }} justifyContent="between">
							<Dialog.Title>Share Operational Bookings</Dialog.Title>
							{!selectedTripId &&
							selectedTripOperationalBookings.length === 1 ? (
								<Box marginRight="8" marginTop={{ xs: "2", sm: "0" }}>
									<Button
										level="secondary"
										status="primary"
										size="sm"
										onClick={() => {
											dispatch({
												selectedTripId: selectedTripOperationalBookings[0].id,
												selectedTripOperationalBookings: [],
											})
										}}
									>
										Select Other Bookings
									</Button>
								</Box>
							) : null}
						</Box>
					</Dialog.Header>
					{selectedTripOperationalBookings.length ? (
						<Box>
							<ShareSelectedTripOperationalBookings
								bookings={selectedTripOperationalBookings}
								canShareWith={canShareWith}
								cabSchedules={cabSchedules}
								travelActivityBookings={travelActivityBookings}
							/>
						</Box>
					) : trip?.id || selectedTripId ? (
						<Dialog.Body>
							<SelectTripOperationalBookingsToShare
								tripId={(trip?.id || selectedTripId) as number}
								onCancel={onCancel}
								onSelect={(schedules) =>
									dispatch({
										selectedTripOperationalBookings: schedules,
										selectedTripId: undefined,
									})
								}
							/>
						</Dialog.Body>
					) : (
						<Box>Please select a trip or a schedule to share</Box>
					)}
				</Dialog>
			) : null}
		</>
	)
}

type TInclusionOptions = {
	prices: boolean
	itinerary: boolean
	arrivalDepartures: boolean
	remarks: boolean
	primaryTourist: boolean
	allTourist: boolean
	hotels: boolean
}

function ShareSelectedTripOperationalBookings({
	bookings,
	canShareWith = DEFAULT_SHARE_WITH,
	cabSchedules,
	travelActivityBookings,
}: {
	bookings: Array<TSharableTripOperationalBooking>
	canShareWith?: ShareWith
	cabSchedules?: Array<ICabSchedule>
	travelActivityBookings?: Array<TTravelActiviytBooking>
}) {
	const xhr = useXHR()
	const id = useId()
	const { hasPermission } = useCheckPermissions()
	const canShareWithSuppliers = hasPermission(
		PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS
	)
	const [shareWithTab, setShareWithTab] = useState<ShareWith[number]>(
		canShareWith[0]
	)
	const [shareVia, setShareVia] = useState<"whatsapp" | "email">("whatsapp")
	const [shareWithDriver, setShareWithDriver] = useState<TDriver | undefined>()
	const [preferredInclusions, setPreferredInclusions] = useSynedStorageState(
		"trip_operational_booking_sharing_inclusions",
		{
			prices: true,
			itinerary: false,
			arrivalDepartures: true,
			remarks: true,
			primaryTourist: true,
			allTourist: true,
			hotels: true,
		}
	)
	const [shareWithTrip, setShareWithTrip] = useState<
		TTripOperationalBooking | undefined
	>()
	const [
		{
			prices: includePrices,
			itinerary: includeItinerary,
			arrivalDepartures: includeArrivalDepartures,
			remarks: includeRemarks,
			primaryTourist: includePrimaryTourist,
			allTourist: includeAllTourist,
			hotels: includeHotels,
		},
		toggleInclusions,
	] = useReducer(
		(state: TInclusionOptions, actions: Partial<TInclusionOptions>) => ({
			...state,
			...actions,
		}),
		{
			prices: preferredInclusions.prices,
			itinerary: preferredInclusions.itinerary,
			arrivalDepartures: preferredInclusions.arrivalDepartures,
			remarks: preferredInclusions.remarks,
			primaryTourist: preferredInclusions.primaryTourist,
			allTourist: preferredInclusions.allTourist,
			hotels: preferredInclusions.hotels,
		}
	)

	useEffect(() => {
		setPreferredInclusions({
			prices: includePrices,
			itinerary: includeItinerary,
			arrivalDepartures: includeArrivalDepartures,
			remarks: includeRemarks,
			primaryTourist: includePrimaryTourist,
			allTourist: includeAllTourist,
			hotels: includeHotels,
		})
	}, [
		includePrices,
		includeItinerary,
		includeArrivalDepartures,
		includeRemarks,
		includePrimaryTourist,
		setPreferredInclusions,
		includeAllTourist,
		includeHotels,
	])

	const [
		shareWithTransportServiceProvider,
		setShareWithTransportServiceProvider,
	] = useState<ISupplier | undefined>()

	const { data } = useSWR(
		`/api/trip-operational-bookings/${bookings.map((s) => s.id).join("-")}?${id}`,
		() =>
			Promise.all(
				bookings.map((s) => {
					const selectedCabSchedules = cabSchedules
						? cabSchedules.map((s) => s.id)
						: (s?.daywise_services || []).flatMap((s) =>
								s.services.map((s) => s.cab_schedule?.id).filter(Boolean)
							)
					const selectedTravelActivityBookings = travelActivityBookings
						? travelActivityBookings.map((t) => t.id)
						: (s?.daywise_services || []).flatMap((s) =>
								s.services
									.map((s) => s.travel_activity_booking?.id)
									.filter(Boolean)
							)
					return xhr.get<{ data: TTripOperationalBooking }>(
						`/trip-operational-bookings/${s.id}`,
						{
							params: {
								cab_schedules: selectedCabSchedules.length
									? selectedCabSchedules
									: [0], // ensure that we pass something unexpected, otherwise, we will get all bookings
								travel_activity_bookings: selectedTravelActivityBookings.length
									? selectedTravelActivityBookings
									: [0], // ensure that we pass something unexpected, otherwise, we will get all bookings
								include: "itinerary,hotels,arrival_departure,tenant_brand",
							},
						}
					)
				})
			).then((schedules) => {
				return schedules.map((s) => s.data.data)
			})
	)
	// filter out the schedules that the user can modify
	const modifiableSchedules = useMemo(
		() => (!data ? [] : data.filter((s) => s.can_modify)),
		[data]
	)
	const whatsappTextForBooking = useMemo(
		() =>
			getWhatsAppMessageContentForBooking(modifiableSchedules, {
				includePrices,
				includeItinerary,
				includeRemarks,
				includeAllTourist,
			}),
		[
			modifiableSchedules,
			includePrices,
			includeItinerary,
			includeRemarks,
			includeAllTourist,
		]
	)
	const whatsappTextForGuestPerTrip = useMemo(
		() =>
			!data
				? {}
				: getWhatsAppMessageContentForGuestPerTrip(data, {
						includeItinerary,
						includeRemarks,
						includeAllTourist,
					}),
		[data, includeItinerary, includeRemarks, includeAllTourist]
	)
	const whatsappTextPerDriver: { [driverId: string]: string } = useMemo(
		() =>
			getWhatsAppMessageContentForDrivers(modifiableSchedules, {
				itinerary: includeItinerary,
				arrivalDepartures: includeArrivalDepartures,
				remarks: includeRemarks,
				primaryTourist: includePrimaryTourist,
				allTourist: includeAllTourist,
			}),
		[
			modifiableSchedules,
			includeItinerary,
			includeArrivalDepartures,
			includeRemarks,
			includePrimaryTourist,
			includeAllTourist,
		]
	)
	const whatsappTextPerTransportServiceProvider: {
		[tspId: string]: string
	} = useMemo(
		() =>
			getWhatsAppMessageContentForTransportServiceProviders(
				modifiableSchedules,
				{
					prices: includePrices,
					itinerary: includeItinerary,
					arrivalDepartures: includeArrivalDepartures,
					remarks: includeRemarks,
					primaryTourist: includePrimaryTourist,
					allTourist: includeAllTourist,
					hotels: includeHotels,
				}
			),
		[
			modifiableSchedules,
			includePrices,
			includeItinerary,
			includeArrivalDepartures,
			includeRemarks,
			includePrimaryTourist,
			includeAllTourist,
			includeHotels,
		]
	)
	const trips = data
	const drivers = useMemo(() => {
		const drivers: Array<TDriver> = []
		modifiableSchedules
			.flatMap((s) => s.cab_schedules || [])
			.forEach(function (schedule) {
				schedule.cabs.forEach((cab) => {
					const { driver } = cab
					if (driver && drivers.findIndex((d) => d.id === driver.id) === -1) {
						drivers.push(driver)
					}
				})
			})
		return drivers
	}, [modifiableSchedules])
	const transportServiceProviders = useMemo(() => {
		return getTransportServiceProvidersWithBookings(modifiableSchedules)
			.transportServiceProviders
	}, [modifiableSchedules])
	const hasAnythingBooked = useMemo(() => {
		if (!data) return false
		return data.some(
			(data) =>
				data.cab_schedules?.some((s) => s.cabs_booked) ||
				data.travel_activity_bookings?.some((s) => s.is_booked)
		)
	}, [data])
	const hasAnythingAssigned = useMemo(() => {
		if (!data) return false
		return data.some(
			(data) =>
				data.cab_schedules?.some((s) =>
					s.cabs.some((s) => s.transport_service_provider)
				) || data.travel_activity_bookings?.some((s) => s.supplier)
		)
	}, [data])

	const hasAnyTouristInBookings = useMemo((): boolean => {
		if (shareWithTab === "booking") {
			toggleInclusions({ allTourist: false })
		}
		if (!data) return false
		for (const d of data) {
			const { cab_schedules, travel_activity_bookings } = d
			const hasCabTourist = cab_schedules?.some(
				(cs) => (cs.tourists?.length ?? 0) > 1
			)
			const hasTravelActivityTourist = travel_activity_bookings?.some(
				(tab) => (tab.tourists?.length ?? 0) > 1
			)
			if (hasCabTourist || hasTravelActivityTourist) {
				return true
			}
		}
		return false
	}, [data, shareWithTab])

	useEffect(() => {
		// set the first booked trip when sharing with guest and
		// trip is not already set
		if (
			trips &&
			trips?.length !== 0 &&
			shareWithTab === "guest" &&
			shareWithTrip === undefined
		) {
			setShareWithTrip(trips[0])
		}
	}, [trips, data, shareWithTab, shareWithTrip])
	useEffect(() => {
		// set the first tsp when sharing with transportServiceProvider and
		// tsp is not already set
		if (
			data &&
			transportServiceProviders.length !== 0 &&
			shareWithTab === "transportServiceProvider" &&
			shareWithTransportServiceProvider === undefined
		) {
			setShareWithTransportServiceProvider(transportServiceProviders[0])
		}
	}, [
		transportServiceProviders,
		data,
		shareWithTab,
		shareWithTransportServiceProvider,
	])
	useEffect(() => {
		// set the first driver when sharing with driver and
		// driver is not already set
		if (
			data &&
			drivers.length !== 0 &&
			shareWithTab === "driver" &&
			shareWithDriver === undefined
		) {
			setShareWithDriver(drivers[0])
		}
	}, [drivers, data, shareWithTab, shareWithDriver])
	useEffect(() => {
		// default active is `Share with Guest`
		// Set the active tab to `booking` if no schedules are booked
		if (data && !hasAnythingBooked && shareWithTab === "guest") {
			if (
				hasAnythingAssigned &&
				canShareWith.indexOf("transportServiceProvider") !== -1
			) {
				setShareWithTab("transportServiceProvider")
				setShareVia("whatsapp")
			} else if (
				modifiableSchedules.length > 0 &&
				canShareWith.indexOf("booking") !== -1
			) {
				setShareWithTab("booking")
				setShareVia("whatsapp")
			}
		}
	}, [
		hasAnythingBooked,
		hasAnythingAssigned,
		data,
		shareWithTab,
		modifiableSchedules,
		canShareWith,
	])
	if (!data) {
		return (
			<Box>
				<Spinner alignCenter padding="4" />
			</Box>
		)
	}
	if (!hasAnythingBooked && modifiableSchedules.length === 0) {
		return (
			<Box padding="8" textAlign="center">
				<Box fontWeight="semibold" marginBottom="4">
					No Schedules to Share
				</Box>
				<Box as="p" color="muted">
					Selected schedule(s) have not been booked, hence can not be shared
					with guest.
				</Box>
			</Box>
		)
	}
	return (
		<Tabs layout="col" gap="0" position="relative">
			<TabsList>
				{hasAnythingBooked && canShareWith.indexOf("guest") !== -1 && trips ? (
					<TabItem
						active={shareWithTab === "guest"}
						onClick={() => {
							setShareWithTab("guest")
							setShareWithTrip(trips[0])
							setShareVia("whatsapp")
						}}
					>
						{pluralize("Guests", trips.length)}
					</TabItem>
				) : null}
				{transportServiceProviders.length > 0 &&
				canShareWith.indexOf("transportServiceProvider") !== -1 ? (
					<TabItem
						active={shareWithTab === "transportServiceProvider"}
						onClick={() => {
							setShareWithTab("transportServiceProvider")
							setShareWithTransportServiceProvider(transportServiceProviders[0])
							setShareVia("whatsapp")
						}}
					>
						{pluralize("Service Provider", transportServiceProviders.length)}
					</TabItem>
				) : null}
				{drivers.length > 0 && canShareWith.indexOf("driver") !== -1 ? (
					<TabItem
						active={shareWithTab === "driver"}
						onClick={() => {
							setShareWithTab("driver")
							setShareWithDriver(drivers[0])
							setShareVia("whatsapp")
						}}
					>
						{pluralize("Driver", drivers.length)}
					</TabItem>
				) : null}
				{modifiableSchedules.length &&
				canShareWith.indexOf("booking") !== -1 ? (
					<TabItem
						active={shareWithTab === "booking"}
						onClick={() => {
							setShareWithTab("booking")
							setShareVia("whatsapp")
						}}
					>
						Booking Enquiry
					</TabItem>
				) : null}
			</TabsList>
			<Box
				padding="4"
				bgColor="subtle"
				position="sticky"
				top="0"
				zIndex="10"
				borderBottomWidth="1"
				boxShadow="sm"
			>
				<Inline gap="4" flexWrap="wrap">
					{shareWithTab !== "guest" && shareWithTab !== "driver" ? (
						<Inline as="label" gap="2" alignItems="center">
							<CheckboxInput
								name="include_price"
								checked={includePrices}
								onChange={() => toggleInclusions({ prices: !includePrices })}
							/>
							<Text>Price</Text>
						</Inline>
					) : null}
					<Inline as="label" gap="2" alignItems="center">
						<CheckboxInput
							checked={includeItinerary}
							name="include_itinerary"
							onChange={() =>
								toggleInclusions({ itinerary: !includeItinerary })
							}
						/>
						<Text>Itinerary</Text>
					</Inline>
					<Inline as="label" gap="2" alignItems="center">
						<CheckboxInput
							checked={includeRemarks}
							name="include_remarks"
							onChange={() => toggleInclusions({ remarks: !includeRemarks })}
						/>
						<Text>Remarks</Text>
					</Inline>
					{shareWithTab !== "guest" && shareWithTab !== "booking" ? (
						<Inline as="label" gap="2" alignItems="center">
							<CheckboxInput
								name="include_tourists"
								checked={includePrimaryTourist}
								onChange={() =>
									toggleInclusions({ primaryTourist: !includePrimaryTourist })
								}
							/>
							<Text>Guest</Text>
						</Inline>
					) : null}
					{shareWithTab !== "booking" && hasAnyTouristInBookings ? (
						<Inline as="label" gap="2" alignItems="center">
							<CheckboxInput
								checked={includeAllTourist}
								name="include_guest_list"
								onChange={() =>
									toggleInclusions({ allTourist: !includeAllTourist })
								}
							/>
							<Text>Guest List</Text>
						</Inline>
					) : null}
					{shareWithTab === "transportServiceProvider" ? (
						<Inline as="label" gap="2" alignItems="center">
							<CheckboxInput
								checked={includeHotels}
								name="include_hotels"
								onChange={() => toggleInclusions({ hotels: !includeHotels })}
							/>
							<Text>Hotels</Text>
						</Inline>
					) : null}
				</Inline>
			</Box>
			<TabContent active={shareWithTab === "booking"} key="booking_whatsapp">
				<Box paddingX="4" paddingY="2">
					<Text color="muted">
						Please share it with your respective{" "}
						<b>transport service provider</b>.
					</Text>
				</Box>
				<Box roundedBottom="md" overflow="hidden">
					<Tabs layout="col" gap="0">
						<TabsList>
							<TabItem
								onClick={() => setShareVia("whatsapp")}
								active={shareVia === "whatsapp"}
							>
								<Icons.Annotation /> WhatsApp
							</TabItem>
							<TabItem
								onClick={() => setShareVia("email")}
								active={shareVia === "email"}
							>
								<Icons.Mail /> Email
							</TabItem>
						</TabsList>
						<TabContent active={shareVia === "whatsapp"}>
							<ShareViaWhatsApp
								text={whatsappTextForBooking}
								trackingContentType={TrackingShareContentType}
								trackingContentId="booking_enquiry"
							/>
						</TabContent>
						{canShareWithSuppliers ? (
							<TabContent active={shareVia === "email"}>
								<EmailShare shareWith="booking_enquiry">
									<EmailContentForBooking
										bookings={data}
										includePrices={includePrices}
										includeItinerary={includeItinerary}
										includeRemarks={includeRemarks}
										includeAllTourist={includeAllTourist}
									/>
								</EmailShare>
							</TabContent>
						) : null}
					</Tabs>
				</Box>
			</TabContent>
			{trips && trips.length > 1 && shareWithTab === "guest" ? (
				<Stack>
					<Box paddingX="4" paddingY="2">
						<Text>Please select the guest you want to share details with:</Text>
					</Box>
					<Tabs layout="col">
						<TabsList>
							{trips.map((trip) => (
								<TabItem
									key={trip.id}
									active={shareWithTrip?.id === trip.id}
									onClick={() => {
										setShareWithTrip(trip)
									}}
								>
									{trip.contact.name}
								</TabItem>
							))}
						</TabsList>
					</Tabs>
				</Stack>
			) : null}
			{transportServiceProviders.length > 1 &&
			shareWithTab === "transportServiceProvider" &&
			canShareWithSuppliers ? (
				<Stack>
					<Box paddingX="4" paddingY="2">
						<Text>
							Please select the service provider you want to share details with:
						</Text>
					</Box>
					<Tabs layout="col">
						<TabsList>
							{transportServiceProviders.map((d) => (
								<TabItem
									key={d.id}
									active={shareWithTransportServiceProvider?.id === d.id}
									onClick={() => {
										setShareWithTransportServiceProvider(d)
									}}
								>
									<Icons.UserGroup /> {d.name}
								</TabItem>
							))}
						</TabsList>
					</Tabs>
				</Stack>
			) : null}
			{drivers.length > 1 &&
			shareWithTab === "driver" &&
			canShareWithSuppliers ? (
				<Stack>
					<Box paddingX="4" paddingY="2">
						<Text>
							Please select the driver you want to share details with:
						</Text>
					</Box>
					<Tabs layout="col">
						<TabsList>
							{drivers.map((d) => (
								<TabItem
									key={d.id}
									active={shareWithDriver?.id === d.id}
									onClick={() => {
										setShareWithDriver(d)
									}}
								>
									{d.name}
								</TabItem>
							))}
						</TabsList>
					</Tabs>
				</Stack>
			) : null}
			{trips?.map((trip) => (
				<TabContent
					active={
						shareWithTab === "guest" &&
						shareVia === "whatsapp" &&
						shareWithTrip?.id === trip.id
					}
					key={`guest_${trip.id}_whatsapp`}
				>
					<ShareViaWhatsApp
						text={whatsappTextForGuestPerTrip[trip.id]}
						phoneNumber={trip.contact.phone_numbers}
						trackingContentType={TrackingShareContentType}
						trackingContentId="guest"
						// Send through the tenant's own integration when there is one,
						// taking the message as edited in the preview.
						renderSendAction={({ text, defaultAction }) => (
							<SendOperationalBookingsOnWhatsApp
								tripId={trip.id}
								message={text}
								fallback={defaultAction}
							/>
						)}
					/>
				</TabContent>
			))}
			{canShareWithSuppliers ? (
				<TabContent
					active={shareWithTab === "transportServiceProvider"}
					key={`tsp_whatsapp`}
				>
					<Tabs layout="col">
						<TabsList>
							<TabItem
								onClick={() => setShareVia("whatsapp")}
								active={shareVia === "whatsapp"}
							>
								<Icons.Annotation /> WhatsApp
							</TabItem>
							<TabItem
								onClick={() => setShareVia("email")}
								active={shareVia === "email"}
							>
								<Icons.Mail /> Email
							</TabItem>
						</TabsList>
						<TabContent active={shareVia === "whatsapp"}>
							{transportServiceProviders.map((tsp) => (
								<TabContent
									active={shareWithTransportServiceProvider?.id === tsp.id}
									key={`tsp_${tsp.id}_whatsapp`}
								>
									<ShareViaWhatsApp
										text={whatsappTextPerTransportServiceProvider[tsp.id]}
										phoneNumber={
											tsp.contacts?.length
												? tsp.contacts[0].phone_numbers
												: undefined
										}
										trackingContentType={TrackingShareContentType}
										trackingContentId="transport_service_provider"
									/>
								</TabContent>
							))}
						</TabContent>
						<TabContent active={shareVia === "email"}>
							<EmailContentForTransportServiceProviders
								bookings={data}
								selectedTsp={shareWithTransportServiceProvider}
								includePrices={includePrices}
								includeItinerary={includeItinerary}
								includeRemarks={includeRemarks}
								includePrimaryTourists={includePrimaryTourist}
								includeAllTourist={includeAllTourist}
								includeHotels={includeHotels}
							/>
						</TabContent>
					</Tabs>
				</TabContent>
			) : null}
			{drivers.map((driver) => (
				<TabContent
					active={
						shareWithTab === "driver" &&
						shareVia === "whatsapp" &&
						shareWithDriver?.id === driver.id
					}
					key={`driver_${driver.id}_whatsapp`}
				>
					<ShareViaWhatsApp
						text={whatsappTextPerDriver[driver.id]}
						phoneNumber={driver.phone_number}
						trackingContentType={TrackingShareContentType}
						trackingContentId="driver"
					/>
				</TabContent>
			))}
		</Tabs>
	)
}

type TConsolidatedCab = IScheduledCab & { count: number }

function consolidateCabsByPickup(
	cabs: Array<IScheduledCab>
): TConsolidatedCab[] {
	const consolidated: TConsolidatedCab[] = []
	const mergeable = new Map<string, TConsolidatedCab>()

	for (const cab of cabs) {
		const pickup_time = cab.pickup_time_local || ""
		const trimmedRemarks = cab.remarks?.trim()
		const trimmedSupplierRemarks = cab.supplier_remarks?.trim()

		if (trimmedRemarks) {
			consolidated.push({ ...cab, remarks: trimmedRemarks, count: 1 })
			continue
		}

		if (trimmedSupplierRemarks) {
			consolidated.push({
				...cab,
				supplier_remarks: trimmedSupplierRemarks,
				count: 1,
			})
			continue
		}

		const cabIdentifier = cab.cab?.id
			? `cab-${cab.cab.id}`
			: `cabtype-${cab.cab_type.id}`

		const key = `${cabIdentifier}-${pickup_time}`

		const existing = mergeable.get(key)
		if (existing) {
			existing.count += 1
			continue
		}

		const entry: TConsolidatedCab = { ...cab, count: 1 }
		mergeable.set(key, entry)
		consolidated.push(entry)
	}

	return consolidated
}

function renderWhatsAppLocation({
	use_hotel_as_pickup,
	use_hotel_as_drop,
	checkinHotels,
	checkoutHotels,
	stayHotels,
	pick_up_location,
	drop_location,
	prefix = " :",
	forEmail = false,
	startFromNewLine = true,
}: {
	use_hotel_as_pickup?: string | null
	use_hotel_as_drop?: string | null
	checkinHotels: Array<{ hotel?: IHotel; name: string }>
	checkoutHotels: Array<{ hotel?: IHotel; name: string }>
	stayHotels: Array<{ hotel?: IHotel; name: string }>
	pick_up_location?: { name: string } | null
	drop_location?: { name: string } | null
	prefix?: string
	forEmail?: boolean
	startFromNewLine?: boolean
}) {
	const getHotelData = (type?: string | null) => {
		const source =
			type === "check_in"
				? [...(checkinHotels || []), ...(stayHotels || [])]
				: type === "check_out"
					? checkoutHotels
					: []

		return Array.from(
			new Map(
				(source || []).map((h) => {
					const key = `${h.name}-${h.hotel?.location?.short_name}`
					return [
						key,
						{
							name: h.name,
							location: h.hotel?.location?.short_name,
						},
					]
				})
			).values()
		)
	}

	const getLocationText = (
		useHotel?: string | null,
		hotels?: Array<{ name: string; location?: string }>,
		fallback?: string
	) => {
		if (!useHotel) return fallback || ""
		if (!hotels?.length) return fallback || "" // Fallback if no hotels found for the selected type
		return hotels
			.map((h) => `${h.name}${h.location ? `, ${h.location}` : ""}`)
			.join(" / ")
	}

	const pickupText = getLocationText(
		use_hotel_as_pickup,
		getHotelData(use_hotel_as_pickup),
		pick_up_location?.name
	)
	const dropText = getLocationText(
		use_hotel_as_drop,
		getHotelData(use_hotel_as_drop),
		drop_location?.name
	)

	let text = ""
	if (pickupText) text += `\n\`\`\`${prefix}\`\`\` *Pickup:* ${pickupText}`
	if (dropText) text += `\n\`\`\`${prefix}\`\`\` *Drop:* ${dropText}`

	return forEmail && (pickupText || dropText) ? (
		<>
			{pickupText ? (
				<>
					{startFromNewLine ? <br /> : null}
					Pickup:{" "}
					<b>
						<small>{pickupText}</small>
					</b>
				</>
			) : null}
			{dropText ? (
				<>
					<br />
					Drop:{" "}
					<b>
						<small>{dropText}</small>
					</b>
				</>
			) : null}
		</>
	) : (
		text
	)
}

function getWhatsAppMessageContentForBooking(
	schedules: Array<TTripOperationalBooking>,
	{
		includePrices,
		includeItinerary,
		includeRemarks,
		includeAllTourist,
	}: {
		includePrices: boolean
		includeItinerary: boolean
		includeRemarks: boolean
		includeAllTourist: boolean
	}
) {
	if (!schedules.length)
		return `There are not schedules you can share via bookings.`
	let totalPrice: Array<TMoney> = []
	return `Hi,
We have a new booking request with following details:
${schedules
	.map((trip) => {
		const info = getSameCabInfo(trip, "host")
		const hasSameCabTypeForAll = info.hasSameCabTypeForAll
		const daywise_services = trip.daywise_services

		// Consolidated cab groups for header display (pickup time always; remarks only when enabled)
		const headerCabs =
			hasSameCabTypeForAll &&
			Array.isArray(trip.cab_schedules) &&
			trip.cab_schedules[0] &&
			trip.cab_schedules[0].cabs
				? consolidateCabsByPickup(trip.cab_schedules[0].cabs)
				: []

		return `\`\`\`-----------\`\`\`
\`\`\`Trip Id   :\`\`\` ${trip.branded_id}
\`\`\`Pax       :\`\`\` *${pluralize("Adult", trip.no_of_adults, true)}${
			trip.children.length
				? ` with ${pluralize(
						"Child",
						trip.children.length,
						true
					)}(${trip.children.map((c) => `${c.age}y`).join(",")})`
				: ``
		}*${
			hasSameCabTypeForAll && headerCabs.length
				? `\n\`\`\`Cab${
						headerCabs.length > 1 ? "s" : " "
					}      :\n\`\`\`${headerCabs
						.map((cab) => {
							const remarksText =
								includeRemarks && (cab.remarks || cab.supplier_remarks)
									? `\n\`\`\`:\`\`\` *Remarks* ${cab.remarks || cab.supplier_remarks}`
									: ""
							return `\`\`\`-\`\`\` *${cab.count}-${cab.cab_type.name}*${remarksText}`
						})
						.join("\n\n")}`
				: ``
		}
\`\`\`-----------\`\`\`
${daywise_services
	.map(
		({
			date,
			date_local,
			services,
			checkout_hotels,
			checkin_hotels,
			stay_hotels,
			self_booked_checkout_hotels,
			self_booked_checkin_hotels,
			self_booked_stay_hotels,
		}) => {
			const { checkinHotels, stayHotels, checkoutHotels } = combineBookings({
				checkout_hotels,
				checkin_hotels,
				stay_hotels,
				self_booked_checkout_hotels,
				self_booked_checkin_hotels,
				self_booked_stay_hotels,
				showSelfBooked: true,
			})
			return `🗓️ *${formatDate(localOrUtcTimestampToLocalDate(date_local, date), "dddd DD MMMM, YYYY")}*

${services
	.map((s) => {
		if (s.cab_schedule) {
			const schedule = s.cab_schedule
			const {
				cabs,
				transport_service,
				remarks,
				supplier_remarks,
				itinerary,
				start_date,
				start_date_local,
				start_time_local,
				has_cab_time_variation: scheduleHasCabTimeVariation,
				tourists,
			} = schedule
			const guests = formatTouristInfoForWhatsapp(tourists)
			const normalizedCabs: TConsolidatedCab[] = scheduleHasCabTimeVariation
				? cabs.map((cab) => ({ ...cab, count: 1 }))
				: consolidateCabsByPickup(cabs)
			const price = getBookingPriceForCabs(cabs)
			totalPrice = addMoneyFromDifferentCurrencies(totalPrice.concat(price))
			const singleCab = cabs.length === 1
			const multipleCabs = cabs.length > 1
			const serviceLocationText =
				!scheduleHasCabTimeVariation || singleCab
					? renderWhatsAppLocation({
							pick_up_location: !scheduleHasCabTimeVariation
								? schedule.pick_up_location
								: cabs[0].pick_up_location,
							drop_location: !scheduleHasCabTimeVariation
								? schedule.drop_location
								: cabs[0].drop_location,
							use_hotel_as_pickup: !scheduleHasCabTimeVariation
								? schedule.use_hotel_as_pickup
								: cabs[0].use_hotel_as_pickup,
							use_hotel_as_drop: !scheduleHasCabTimeVariation
								? schedule.use_hotel_as_drop
								: cabs[0].use_hotel_as_drop,
							checkinHotels,
							checkoutHotels,
							stayHotels,
						})
					: ""

			const servicePickupTimeText =
				showPickup(start_date, start_time_local, start_date_local) &&
				(!scheduleHasCabTimeVariation || singleCab)
					? ` *(Pickup at ${formatDate(start_time_local ? parseDate(start_time_local, "HH:mm:ss") : utcTimestampToLocalDate(start_date), "HH:mm [hrs]")})*`
					: ``

			const shouldShowCabDetails =
				!hasSameCabTypeForAll || (scheduleHasCabTimeVariation && multipleCabs)

			const hasMultipleCabVariation =
				scheduleHasCabTimeVariation && multipleCabs

			const priceText =
				includePrices && price.length
					? `\n\`\`\` -\`\`\` *${price
							.map((p) =>
								formatMoneyByIntl(p, { showSolidus: true, showCurrency: true })
							)
							.join(" + ")}*`
					: ``

			return `\`\`\`-\`\`\` *${transport_service.from_to}*${servicePickupTimeText}
\`\`\` |\`\`\` ${transport_service.service_display_name}${priceText}${serviceLocationText}${includeAllTourist && guests ? `\n\`\`\` |\`\`\` *Guests:* ${guests}` : ``}${
				(remarks || supplier_remarks) && includeRemarks
					? `\n\`\`\` •\`\`\` *Remarks*${(remarks || supplier_remarks || "")
							.split("\n")
							.map((c) => `\n\`\`\` |\`\`\` ${c}`)
							.join("")}`
					: ``
			}${
				shouldShowCabDetails
					? `
\`\`\` *Cabs:* \n\`\`\`${normalizedCabs
							.map((cab) => {
								const pickupTimeDisplay =
									cab.pickup_time_local && hasMultipleCabVariation
										? ` *(Pickup at ${formatDate(parseDate(cab.pickup_time_local, "HH:mm:ss"), "HH:mm [hrs]")})*`
										: ``
								const locationDisplay = hasMultipleCabVariation
									? renderWhatsAppLocation({
											pick_up_location: cab.pick_up_location,
											drop_location: cab.drop_location,
											use_hotel_as_pickup: cab.use_hotel_as_pickup,
											use_hotel_as_drop: cab.use_hotel_as_drop,
											checkinHotels,
											checkoutHotels,
											stayHotels,
											prefix: ":",
										})
									: ``
								const remarksDisplay =
									(cab.remarks || cab.supplier_remarks) && includeRemarks
										? `\n\`\`\`-\`\`\` *Remarks:* ${cab.remarks || cab.supplier_remarks}`
										: ``
								return `*: ${cab.count}-${cab.cab_type.name}*${pickupTimeDisplay}${locationDisplay}${remarksDisplay}`
							})
							.join("\n\n")}`
					: ``
			}${includeItinerary && itinerary?.description ? `\n${itinerary.description}` : ``}`
		}
		if (s.travel_activity_booking) {
			const travelActivityBooking = s.travel_activity_booking
			const {
				start_time_formatted,
				activity,
				ticket_type,
				duration_formatted,
				remarks,
				itinerary,
				tourists,
				booked_price,
				currency,
				ticket_tourist_configurations_full_name_with_complementary,
				use_hotel_as_pickup,
				use_hotel_as_drop,
				pick_up_location,
				drop_location,
			} = travelActivityBooking
			const price = moneyParseByDecimal(Number(booked_price || 0), currency)
			totalPrice = addMoneyFromDifferentCurrencies(totalPrice.concat([price]))
			const guests = formatTouristInfoForWhatsapp(tourists)
			return `\`\`\`-\`\`\` *${activity.name}${ticket_type ? ` - ${ticket_type.display_name}` : ``}*
\`\`\` |\`\`\` *${ticket_tourist_configurations_full_name_with_complementary}*${start_time_formatted || duration_formatted ? ` *(${[start_time_formatted, duration_formatted].filter(Boolean).join(" - ")})*` : ``}${renderWhatsAppLocation(
				{
					use_hotel_as_pickup,
					use_hotel_as_drop,
					checkinHotels,
					checkoutHotels,
					stayHotels,
					pick_up_location,
					drop_location,
					prefix: " :",
				}
			)}${
				remarks && includeRemarks
					? `
\`\`\` •\`\`\` *Remarks*
${remarks
	.split("\n")
	.map((c) => `\`\`\` |\`\`\` ${c}`)
	.join("\n")}`
					: ``
			}${includeAllTourist && guests ? `\n\`\`\` |\`\`\` *Guests:* ${guests}` : ""}${
				includePrices && price && !isZeroMoney(price)
					? `
\`\`\` :\`\`\` ${formatMoneyByIntl(price, { showCurrency: true, showSolidus: true })}`
					: ``
			}${includeItinerary && itinerary?.description ? `\n${itinerary.description}` : ``}`
		}
		return ``
	})
	.join("\n\n")}`
		}
	)
	.join("\n\n--------------\n")}`
	})
	.join("\n\n")}${
		includePrices && totalPrice.length
			? `

-------
*Total Price: ${totalPrice.map((p) => formatMoneyByIntl(p, { showCurrency: true, showSolidus: true })).join(" + ")}*`
			: ``
	}

*Please confirm this booking.*
`
}

function EmailContentForBooking({
	bookings,
	includePrices,
	includeItinerary,
	includeRemarks,
	includeAllTourist,
}: {
	bookings: Array<TTripOperationalBooking>
	includePrices: boolean
	includeItinerary: boolean
	includeRemarks: boolean
	includeAllTourist: boolean
}) {
	let totalPrice: Array<TMoney> = []
	const tenantBrand = bookings[0]?.tenant_brand
	return (
		<>
			<p>Hi,</p>
			{!tenantBrand ? null : (
				<>
					<br />
					<p>
						Greetings from{" "}
						<b>
							{tenantBrand.short_name}
							{tenantBrand.name
								.toLowerCase()
								.indexOf(tenantBrand.short_name.toLowerCase()) === -1
								? ` (${tenantBrand.name})`
								: null}
						</b>
						.
					</p>
				</>
			)}
			<br />
			<p>We have a new booking request with following details:</p>
			<br />
			<table>
				{bookings.map((trip, index) => {
					const info = getSameCabInfo(trip, "host")
					const hasSameCabTypeForAll = info.hasSameCabTypeForAll
					let cabTypeWithCount = info.cabTypeWithCount

					const cols = 3 + (includePrices ? 1 : 0)
					const serviceAreaCols = cols - 1 - (includePrices ? 1 : 0)

					// Get cabs with remarks for display when remarks are enabled
					const cabsWithRemarks =
						includeRemarks && trip.cab_schedules?.[0]?.cabs
							? consolidateCabsByPickup(trip.cab_schedules[0].cabs)
							: []
					return (
						<tbody key={trip.id}>
							{index > 0 ? (
								<>
									<tr>
										<td colSpan={cols}></td>
									</tr>
									<tr>
										<td colSpan={cols}>+++++++++++++++++++++++++</td>
									</tr>
								</>
							) : null}
							<tr>
								<td>Trip Id</td>
								<td colSpan={cols - 1 - (hasSameCabTypeForAll ? 1 : 0)}>Pax</td>
								{hasSameCabTypeForAll ? (
									<td>{pluralize("Cab", cabTypeWithCount.length)}</td>
								) : null}
							</tr>
							<tr>
								<td>{trip.branded_id}</td>
								<th colSpan={cols - 1 - (hasSameCabTypeForAll ? 1 : 0)}>
									{pluralize("Adult", trip.no_of_adults, true)}
									{trip.children.length
										? ` with ${pluralize(
												"Child",
												trip.children.length,
												true
											)} (${trip.children.map((c) => `${c.age}y`).join(",")})`
										: ``}
								</th>
								{hasSameCabTypeForAll ? (
									<td>
										{includeRemarks && cabsWithRemarks.length > 0 ? (
											<>
												{cabsWithRemarks.map((cab, idx) => {
													return (
														<div key={idx}>
															<b>
																{cab.count}-{cab.cab_type.name}
															</b>
															{cab.remarks || cab.supplier_remarks ? (
																<p
																	style={{
																		borderLeft: "2px solid silver",
																		paddingLeft: "4px",
																		marginTop: "4px",
																		marginBottom:
																			idx < cabsWithRemarks.length - 1
																				? "8px"
																				: "0",
																	}}
																>
																	<b>Remarks : </b>
																	<Markdown className="remarks">
																		{cab.remarks || cab.supplier_remarks}
																	</Markdown>
																</p>
															) : null}
														</div>
													)
												})}
											</>
										) : (
											<b>
												{cabTypeWithCount
													.map(({ name, count }) => `${count}-${name}`)
													.join(" + ")}
											</b>
										)}
									</td>
								) : null}
							</tr>
							<tr>
								<td colSpan={cols}>
									<small>Here are the service details:</small>
								</td>
							</tr>
							<tr key="heading">
								<th>Date</th>
								<th colSpan={serviceAreaCols}>Service</th>
								{includePrices ? (
									<th style={{ textAlign: "right" }}>Price</th>
								) : null}
							</tr>
							{trip.daywise_services.map((dayData, i) => {
								const { date, date_local, services } = dayData
								const { checkinHotels, stayHotels, checkoutHotels } =
									combineBookings({
										...dayData,
										showSelfBooked: true,
									})
								return (
									<Fragment key={i}>
										{services.map((s, index) => {
											const dateRow =
												index === 0 ? (
													<tr>
														<td rowSpan={services.length + 1}>
															<b>
																{formatDate(
																	localOrUtcTimestampToLocalDate(
																		date_local,
																		date
																	),
																	"DD MMM"
																)}
															</b>
															<br />
															<small style={{ whiteSpace: "nowrap" }}>
																{formatDate(
																	localOrUtcTimestampToLocalDate(
																		date_local,
																		date
																	),
																	"ddd YYYY"
																)}
															</small>
														</td>
													</tr>
												) : null
											if (s.cab_schedule) {
												const schedule = s.cab_schedule
												const {
													cabs,
													start_date,
													start_date_local,
													start_time_local,
													transport_service,
													remarks,
													supplier_remarks,
													itinerary,
													tourists,
													has_cab_time_variation,
												} = schedule
												if (!hasSameCabTypeForAll) {
													const cabsCountByName = cabs.reduce<{
														[cab: string]: number
													}>((cabTypeWithCount, cab) => {
														const key = cab.cab_type.name
														if (!cabTypeWithCount[key]) {
															cabTypeWithCount[key] = 0
														}
														cabTypeWithCount[key] += 1
														return cabTypeWithCount
													}, {})
													cabTypeWithCount = Object.keys(cabsCountByName).map(
														(name) => ({ name, count: cabsCountByName[name] })
													)
												}
												const normalizedCabs: TConsolidatedCab[] =
													has_cab_time_variation
														? cabs.map((cab) => ({ ...cab, count: 1 }))
														: consolidateCabsByPickup(cabs)
												const price = getBookingPriceForCabs(cabs)
												totalPrice = addMoneyFromDifferentCurrencies(
													totalPrice.concat(price)
												)
												const singleCab = cabs.length === 1
												const multipleCabs = cabs.length > 1
												return (
													<Fragment key={schedule.id}>
														{dateRow}
														<tr>
															<td
																colSpan={
																	!hasSameCabTypeForAll ||
																	(has_cab_time_variation && multipleCabs)
																		? serviceAreaCols - 1
																		: serviceAreaCols
																}
															>
																<b>
																	{transport_service.from_to} -{" "}
																	{transport_service.service_display_name}
																</b>
																{showPickup(
																	start_date,
																	start_time_local,
																	start_date_local
																) &&
																(!has_cab_time_variation || singleCab) ? (
																	<>
																		<br />
																		<b>
																			<small>
																				{formatDate(
																					start_time_local
																						? parseDate(
																								start_time_local,
																								"HH:mm:ss"
																							)
																						: utcTimestampToLocalDate(
																								start_date
																							),
																					"HH:mm [hrs]"
																				)}
																			</small>
																		</b>
																	</>
																) : null}
																{!has_cab_time_variation || singleCab
																	? renderWhatsAppLocation({
																			use_hotel_as_pickup:
																				has_cab_time_variation
																					? cabs[0].use_hotel_as_pickup
																					: schedule.use_hotel_as_pickup,
																			use_hotel_as_drop: has_cab_time_variation
																				? cabs[0].use_hotel_as_drop
																				: schedule.use_hotel_as_drop,
																			pick_up_location: has_cab_time_variation
																				? cabs[0].pick_up_location
																				: schedule.pick_up_location,
																			drop_location: has_cab_time_variation
																				? cabs[0].drop_location
																				: schedule.drop_location,
																			forEmail: true,
																			checkinHotels,
																			checkoutHotels,
																			stayHotels,
																		})
																	: null}
																<b>
																	{includeAllTourist && (
																		<div
																			style={{
																				fontSize: "smaller",
																				color: "#555",
																			}}
																		>
																			<TouristInfoEmailView
																				tourists={tourists}
																			/>
																		</div>
																	)}
																</b>
																{(remarks || supplier_remarks) &&
																includeRemarks ? (
																	<p
																		style={{
																			borderLeft: "2px solid silver",
																			paddingLeft: "4px",
																			marginBottom: "4px",
																		}}
																	>
																		<b>Remarks : </b>
																		<Markdown className="remarks">
																			{remarks || supplier_remarks}
																		</Markdown>
																	</p>
																) : null}
																{includeItinerary && itinerary?.description ? (
																	<Markdown className="itinerary">
																		{itinerary.description}
																	</Markdown>
																) : null}
															</td>
															{!hasSameCabTypeForAll ||
															(has_cab_time_variation && multipleCabs) ? (
																<td>
																	{normalizedCabs.map((cab, i) => {
																		const pickupTime = cab.pickup_time_local
																			? formatDate(
																					parseDate(
																						cab.pickup_time_local,
																						"HH:mm:ss"
																					),
																					"HH:mm [hrs]"
																				)
																			: ""
																		return (
																			<>
																				{i > 0 ? (
																					<>
																						<br />
																						<br />
																					</>
																				) : null}
																				<b>
																					{`${cab.count ? cab.count + "-" : ""}${cab.cab_type.name}`}
																					{pickupTime &&
																					has_cab_time_variation &&
																					multipleCabs
																						? ` (${pickupTime})`
																						: ``}
																				</b>
																				{has_cab_time_variation && multipleCabs
																					? renderWhatsAppLocation({
																							use_hotel_as_pickup:
																								cab.use_hotel_as_pickup,
																							use_hotel_as_drop:
																								cab.use_hotel_as_drop,
																							checkinHotels,
																							checkoutHotels,
																							stayHotels,
																							pick_up_location:
																								cab.pick_up_location,
																							drop_location: cab.drop_location,
																							forEmail: true,
																						})
																					: null}
																				{(cab.remarks ||
																					cab.supplier_remarks) &&
																				includeRemarks ? (
																					<p
																						style={{
																							borderLeft: "2px solid silver",
																							paddingLeft: "4px",
																						}}
																					>
																						<b>Remarks : </b>
																						<Markdown className="remarks">
																							{cab.remarks ||
																								cab.supplier_remarks}
																						</Markdown>
																					</p>
																				) : null}
																			</>
																		)
																	})}
																</td>
															) : null}
															{includePrices ? (
																<td style={{ textAlign: "right" }}>
																	{price.length
																		? `${price.map((p) => formatMoneyByIntl(p, { showCurrency: true })).join(" + ")}`
																		: null}
																</td>
															) : null}
														</tr>
													</Fragment>
												)
											}

											if (s.travel_activity_booking) {
												const travelActivityBooking = s.travel_activity_booking
												const {
													activity,
													ticket_type,
													ticket_tourist_configurations_full_name_with_complementary,
													start_time_formatted,
													duration_formatted,
													booked_price,
													currency,
													remarks,
													itinerary,
													use_hotel_as_pickup,
													use_hotel_as_drop,
													pick_up_location,
													drop_location,
												} = travelActivityBooking
												const price = moneyParseByDecimal(
													Number(booked_price || 0),
													currency
												)
												totalPrice = addMoneyFromDifferentCurrencies(
													totalPrice.concat([price])
												)
												return (
													<Fragment key={travelActivityBooking.id}>
														{dateRow}
														<tr>
															<td colSpan={serviceAreaCols - 1}>
																<b>
																	{activity.name}
																	{ticket_type
																		? ` - ${ticket_type.display_name}`
																		: null}
																	{includeAllTourist && (
																		<div
																			style={{
																				fontSize: "smaller",
																				color: "#555",
																			}}
																		>
																			<TouristInfoEmailView
																				tourists={
																					travelActivityBooking.tourists
																				}
																			/>
																		</div>
																	)}
																</b>
																{start_time_formatted || duration_formatted ? (
																	<>
																		<br />
																		<b>
																			<small>
																				{[
																					start_time_formatted,
																					duration_formatted,
																				]
																					.filter(Boolean)
																					.join(" - ")}
																			</small>
																		</b>
																	</>
																) : null}
																{renderWhatsAppLocation({
																	pick_up_location,
																	drop_location,
																	use_hotel_as_pickup,
																	use_hotel_as_drop,
																	checkinHotels,
																	stayHotels,
																	checkoutHotels,
																	forEmail: true,
																})}
																{remarks && includeRemarks ? (
																	<>
																		<br />
																		<p
																			style={{
																				fontWeight: "bold",
																				borderLeft: "2px solid silver",
																				paddingLeft: "4px",
																			}}
																		>
																			<small
																				style={{
																					whiteSpace: "pre-line",
																				}}
																			>
																				{remarks}
																			</small>
																		</p>
																	</>
																) : null}
																{includeItinerary && itinerary?.description ? (
																	<Markdown className="itinerary">
																		{itinerary.description}
																	</Markdown>
																) : null}
															</td>
															<td>
																{
																	ticket_tourist_configurations_full_name_with_complementary
																}
															</td>
															{includePrices ? (
																<td style={{ textAlign: "right" }}>
																	{price
																		? `${formatMoneyByIntl(price, { showCurrency: true })}`
																		: null}
																</td>
															) : null}
														</tr>
													</Fragment>
												)
											}
											return null
										})}
									</Fragment>
								)
							})}
						</tbody>
					)
				})}
			</table>
			{includePrices && totalPrice.length ? (
				<>
					<br />
					<p>
						<b>
							Total Price:{" "}
							{totalPrice
								.map((p) =>
									formatMoneyByIntl(p, {
										showCurrency: true,
										showSolidus: true,
									})
								)
								.join(" + ")}
						</b>
					</p>
				</>
			) : null}
			<br />
			<p>
				<b>Please confirm this booking.</b>
			</p>
		</>
	)
}

function getWhatsAppMessageContentForGuestPerTrip(
	schedules: Array<TTripOperationalBooking>,
	{
		includeItinerary,
		includeRemarks,
		includeAllTourist,
	}: {
		includeItinerary: boolean
		includeRemarks: boolean
		includeAllTourist: boolean
	}
) {
	const isAnyActivityBooking = schedules.some((s) =>
		s?.travel_activity_bookings?.some((a) => Boolean(a.booked))
	)
	if (isAnyActivityBooking) {
		return getWhatsAppMessageContentForGuestPerTripForActivities(schedules, {
			includeItinerary,
			includeRemarks,
			includeAllTourist,
		})
	}
	return schedules.reduce<{ [TripId: string]: string }>(
		(messagePerTrip, trip) => {
			const { id, contact, no_of_adults, children } = trip
			const schedulesPerTrip = trip.daywise_services || []
			const schedulesPerDriverCabs = schedulesPerTrip.reduce<
				Array<{
					id: string
					driversAndCabs: Array<{
						driver: TDriver | undefined
						cab?: ICab
						cab_type: ICabType
						remarks: string | undefined
						customer_remarks: string | undefined
						pickup_time: string
						tourists: TTourist[] | undefined
						use_hotel_as_pickup?: string | null
						use_hotel_as_drop?: string | null
						pick_up_location?: TTransportServiceLocationPoint | null
						drop_location?: TTransportServiceLocationPoint | null
						has_cab_time_variation?: boolean
					}>
					daywiseServices: Array<
						Omit<
							TTripOperationalBooking["daywise_services"][number],
							"services"
						> & {
							services: Array<
								Required<
									TTripOperationalBooking["daywise_services"][number]["services"][number]
								>["cab_schedule"]
							>
						}
					>
				}>
			>((schedulesPerDriverCabs, scheduleData) => {
				const driversAndCabs = collect(
					scheduleData.services
						.flatMap((s) =>
							(s.cab_schedule ? s.cab_schedule.cabs || [] : []).map((c) => ({
								...c,
								has_cab_time_variation: s.cab_schedule?.has_cab_time_variation,
							}))
						)
						.filter(
							(
								c
							): c is Required<IScheduledCab, "booked"> & {
								has_cab_time_variation: boolean | undefined
							} => Boolean(c.booked)
						)
						.map((c) => ({
							driver: c.driver,
							cab: c.cab,
							cab_type: c.cab_type,
							remarks: c.remarks,
							customer_remarks: c.customer_remarks,
							tourists: c.tourists,
							pickup_time: c.pickup_time_local || "",
							use_hotel_as_pickup: c.use_hotel_as_pickup,
							use_hotel_as_drop: c.use_hotel_as_drop,
							pick_up_location: c.pick_up_location,
							drop_location: c.drop_location,
							has_cab_time_variation: c.has_cab_time_variation,
						}))
				)
					.unique(
						(c) =>
							`${`d${c?.driver?.id}-${c.cab ? `c${c.cab.id}` : `ct${c.cab_type.id}`}-pt${c.pickup_time || "none"}-r${c.remarks || "none"}-pu${c.use_hotel_as_pickup || "0"}-dr${c.use_hotel_as_drop || "0"}-pl${c.pick_up_location?.name || "none"}-dl${c.drop_location?.name || "none"}-cr${c.customer_remarks || "none"}-hv${c.has_cab_time_variation ? "1" : "0"}`}`
					)
					.toArray()
				if (!driversAndCabs.length) return schedulesPerDriverCabs
				const id = driversAndCabs
					.reduce<
						[
							drivers: Array<{ id: number } | undefined>,
							cabs: Array<{ id: number }>,
							pickupTimes: Array<string>,
							remarks: Array<string>,
							tourists: TTourist[] | undefined,
							customer_remarks: Array<string>,
						]
					>(
						(data, d) => {
							data[0].push(d.driver ?? undefined)
							data[1].push(d.cab || d.cab_type)
							data[2].push(d.pickup_time || "")
							data[3].push(d.remarks || "")
							data[4]?.push(...(d.tourists ?? []))
							data[5]?.push(d.customer_remarks || "")
							return data
						},
						[[], [], [], [], [], []]
					)
					.map((d, index) => {
						if (index === 2 || index === 3 || index === 5) {
							// pickupTimes or remarks array - all strings
							return (d as Array<string>)
								.sort((a, b) => (a > b ? 1 : a < b ? -1 : 0))
								.join(",")
						} else {
							// drivers or cabs array - objects with id
							return (d as Array<{ id: number } | undefined>)
								.sort((a, b) => {
									if (!a || !b) return 0
									return a.id > b.id ? 1 : a.id < b.id ? -1 : 0
								})
								.map((a) => a?.id)
								.join(",")
						}
					})
					.join("-")

				const existingDataIndex = schedulesPerDriverCabs.findIndex(
					(d) => d.id === id
				)

				const data = {
					...scheduleData,
					services: scheduleData.services
						.map((s) =>
							s.cab_schedule
								? {
										...s.cab_schedule,
										cabs: s.cab_schedule.cabs.filter((c) => {
											const {
												driver,
												cab_type,
												cab,
												booked,
												pickup_time_local,
												remarks,
												customer_remarks,
											} = c
											if (!booked) return false
											return driversAndCabs.some(
												(dc) =>
													dc.driver?.id === driver?.id &&
													(dc.cab
														? dc.cab.id === cab?.id
														: dc.cab_type.id === cab_type.id) &&
													(dc.pickup_time || "") ===
														(pickup_time_local || "") &&
													(dc.remarks || "") === (remarks || "") &&
													(dc.customer_remarks || "") ===
														(customer_remarks || "")
											)
										}),
									}
								: undefined
						)
						.filter((s): s is Exclude<typeof s, undefined> => Boolean(s))
						.filter((s) => s.cabs.length),
				}
				if (existingDataIndex === -1) {
					schedulesPerDriverCabs.push({
						id,
						driversAndCabs,
						daywiseServices: [data],
					})
				} else {
					schedulesPerDriverCabs[existingDataIndex].daywiseServices.push(data)
				}
				return schedulesPerDriverCabs
			}, [])
			const guestName = contact?.name || "N/A"
			messagePerTrip[id] = `Dear ${guestName},
Here are the tour operational details for your *Trip Id ${trip.branded_id}*.

\`\`\`Pax Details :\`\`\` *${pluralize("Adult", no_of_adults, true)}*${
				children.length
					? ` with *${pluralize("Child", children.length, true)} (${children
							.map((c) => `${c.age}y`)
							.join(",")})*`
					: ""
			}

${schedulesPerDriverCabs
	.map(({ driversAndCabs, daywiseServices }) => {
		const { checkinHotels, stayHotels, checkoutHotels } = combineBookings({
			...daywiseServices[0],
			showSelfBooked: true,
		})
		return `*Driver/Cab*
${driversAndCabs
	.map(
		({
			driver,
			cab,
			cab_type,
			remarks,
			customer_remarks,
			pickup_time,
			tourists,
			use_hotel_as_pickup,
			use_hotel_as_drop,
			pick_up_location,
			drop_location,
			has_cab_time_variation,
		}) => {
			const guests = formatTouristInfoForWhatsapp(tourists)
			return `\`\`\`•\`\`\` *${driver?.name || "Missing Driver Details"}*${
				driver?.phone_number ? ` (${driver.phone_number})` : ``
			}
\`\`\`-\`\`\`  ${
				cab_type?.display_name || cab
					? `*${
							cab ? `${cab.name} - ${cab.number_plate}` : cab_type.display_name
						}*`
					: ""
			} ${pickup_time && has_cab_time_variation ? `*(Pickup at ${formatDate(parseDate(pickup_time, "HH:mm:ss"), "HH:mm [hrs]")})*` : ""}${
				has_cab_time_variation
					? renderWhatsAppLocation({
							pick_up_location,
							drop_location,
							use_hotel_as_pickup,
							use_hotel_as_drop,
							checkinHotels,
							checkoutHotels,
							stayHotels,
							prefix: " :",
						})
					: ""
			}
${(remarks || customer_remarks) && includeRemarks ? `\`\`\`:\`\`\`  *Remarks* ${`: ${remarks || customer_remarks}`}` : ""}${includeAllTourist && guests ? `\`\`\` -\`\`\` *Guests:* ${guests}` : ""}
`
		}
	)
	.join("\n")}
_will handle following ${pluralize("service", schedules.length)}:_

${daywiseServices
	.map(
		(
			{
				date,
				date_local,
				checkin_hotels,
				checkout_hotels,
				arrival_details,
				departure_details,
				stay_hotels,
				self_booked_checkin_hotels,
				self_booked_checkout_hotels,
				self_booked_stay_hotels,
				services,
			},
			index
		) => {
			const { checkinHotels, stayHotels, checkoutHotels } = combineBookings({
				checkout_hotels,
				checkin_hotels,
				stay_hotels,
				self_booked_checkout_hotels,
				self_booked_checkin_hotels,
				self_booked_stay_hotels,
				showSelfBooked: true,
			})
			const whatsappForHotels = getWhatsAppContentForHotels(
				{
					checkin_hotels,
					checkout_hotels,
					stay_hotels,
					self_booked_checkin_hotels,
					self_booked_checkout_hotels,
					self_booked_stay_hotels,
				},
				index > 0 ? daywiseServices[index - 1] : undefined,
				true
			)
			return `🗓️ *${withOrdinalSuffix(
				getDiff(
					localOrUtcTimestampToLocalDate(date_local, date),
					localOrUtcTimestampToLocalDate(
						trip.start_date_local,
						trip.start_date
					),
					"days"
				) + 1
			)} Day* (${formatDate(localOrUtcTimestampToLocalDate(date_local, date), "ddd DD MMM")})${
				arrival_details?.length
					? `
*Arrival Details:*
${arrival_details
	.map(
		(d) =>
			`\`\`\`- ${formatDate(
				localOrUtcTimestampToLocalDate(d.date_time_local, d.date_time),
				"ddd DD MMM [at] HH:mm [hrs]"
			)}\`\`\`\n_${d.description}_`
	)
	.join("\n")}\n`
					: ``
			}
${services
	.map((cab_schedule) => {
		const {
			start_date,
			start_date_local,
			start_time_local,
			transport_service,
			remarks,
			customer_remarks,
			itinerary,
			tourists,
			has_cab_time_variation: serviceHasCabTimeVariation,
		} = cab_schedule
		const guests = formatTouristInfoForWhatsapp(tourists)
		return `\`\`\`-\`\`\` *${transport_service.from_to}*${
			showPickup(start_date, start_time_local, start_date_local) &&
			!serviceHasCabTimeVariation
				? ` *(Pickup at ${formatDate(start_time_local ? parseDate(start_time_local, "HH:mm:ss") : utcTimestampToLocalDate(start_date), "HH:mm [hrs]")})*`
				: ``
		} - ${transport_service.service_display_name}${
			!serviceHasCabTimeVariation
				? renderWhatsAppLocation({
						pick_up_location: cab_schedule.pick_up_location,
						drop_location: cab_schedule.drop_location,
						use_hotel_as_pickup: cab_schedule.use_hotel_as_pickup,
						use_hotel_as_drop: cab_schedule.use_hotel_as_drop,
						checkinHotels,
						checkoutHotels,
						stayHotels,
						prefix: " :",
					})
				: ""
		}${
			(remarks || customer_remarks) && includeRemarks
				? `
\`\`\` •\`\`\` *Remarks*
${(remarks || customer_remarks || "")
	.split("\n")
	.map((c) => `\`\`\` |\`\`\` ${c}`)
	.join("\n")}`
				: ``
		}${includeItinerary && itinerary?.description ? `\n${itinerary.description}` : ``}${includeAllTourist && guests ? `\n\`\`\` |\`\`\` *Guests:* ${guests}` : ``}`
	})
	.join("\n")}${
				whatsappForHotels
					? `

${whatsappForHotels}
`
					: ``
			}${
				departure_details?.length
					? `

*Departure Details:*
${departure_details
	.map(
		(d) =>
			`\`\`\`- ${formatDate(
				localOrUtcTimestampToLocalDate(d.date_time_local, d.date_time),
				"ddd DD MMM [at] HH:mm [hrs]"
			)}\`\`\`\n_${d.description}_`
	)
	.join("\n")}`
					: ``
			}`
		}
	)
	.join("\n\n")}`
	})
	.join(`\n\`\`\`------------\`\`\`\n\n`)}
\`\`\`------------\`\`\`
_For any queries, please contact the trip coordinator._`
			return messagePerTrip
		},
		{}
	)
}

function getWhatsAppMessageContentForGuestPerTripForActivities(
	schedules: Array<TTripOperationalBooking>,
	{
		includeItinerary,
		includeRemarks,
		includeAllTourist,
	}: {
		includeItinerary: boolean
		includeRemarks: boolean
		includeAllTourist: boolean
	}
) {
	return schedules.reduce<{ [TripId: string]: string }>(
		(messagePerTrip, trip) => {
			const { id, contact, no_of_adults, children, daywise_services } = trip
			const daywise_booked_services = daywise_services
				.map((d) => ({
					...d,
					services: d.services
						.map((d) => ({
							...d,
							cab_schedule: d.cab_schedule
								? {
										...d.cab_schedule,
										cabs: d.cab_schedule.cabs.filter(
											(d): d is Required<typeof d, "booked"> =>
												Boolean(d.booked)
										),
									}
								: undefined,
							travel_activity_booking: d.travel_activity_booking?.is_booked
								? d.travel_activity_booking
								: undefined,
						}))
						.filter(
							(d) =>
								d.travel_activity_booking ||
								(d.cab_schedule && d.cab_schedule.cabs.length)
						),
				}))
				.filter((d) => d.services.length)
			const guestName = contact?.name || "N/A"

			messagePerTrip[id] = `Dear ${guestName},
Here are the tour operational details for your *Trip Id ${trip.branded_id}*.

\`\`\`Pax Details :\`\`\` *${pluralize("Adult", no_of_adults, true)}*${
				children.length
					? ` with *${pluralize("Child", children.length, true)} (${children
							.map((c) => `${c.age}y`)
							.join(",")})*`
					: ""
			}

${daywise_booked_services
	.map(
		(
			{
				date,
				date_local,
				services,
				arrival_details,
				departure_details,
				checkin_hotels,
				checkout_hotels,
				stay_hotels,
				self_booked_checkin_hotels,
				self_booked_checkout_hotels,
				self_booked_stay_hotels,
			},
			index
		) => {
			const { checkinHotels, stayHotels, checkoutHotels } = combineBookings({
				checkout_hotels,
				checkin_hotels,
				stay_hotels,
				self_booked_checkout_hotels,
				self_booked_checkin_hotels,
				self_booked_stay_hotels,
				showSelfBooked: true,
			})
			const whatsappForHotels = getWhatsAppContentForHotels(
				{
					checkin_hotels,
					checkout_hotels,
					stay_hotels,
					self_booked_checkin_hotels,
					self_booked_checkout_hotels,
					self_booked_stay_hotels,
				},
				index > 0 ? daywise_booked_services[index - 1] : undefined,
				true
			)
			return `🗓️ *${withOrdinalSuffix(
				getDiff(
					localOrUtcTimestampToLocalDate(date_local, date),
					localOrUtcTimestampToLocalDate(
						trip.start_date_local,
						trip.start_date
					),
					"days"
				) + 1
			)} Day* - *${formatDate(localOrUtcTimestampToLocalDate(date_local, date), "ddd DD MMM, YYYY")}*
---${
				arrival_details?.length
					? `
*Arrival Details:*
${arrival_details
	.map(
		(d) =>
			`\`\`\`- ${formatDate(
				localOrUtcTimestampToLocalDate(d.date_time_local, d.date_time),
				"ddd DD MMM [at] HH:mm [hrs]"
			)}\`\`\`\n_${d.description}_`
	)
	.join("\n")}\n`
					: ``
			}
${services
	.map(({ cab_schedule, travel_activity_booking }) => {
		if (cab_schedule) {
			const {
				transport_service,
				cabs,
				start_date,
				start_date_local,
				start_time_local,
				remarks,
				customer_remarks,
				itinerary,
				has_cab_time_variation,
			} = cab_schedule
			return `\`\`\`-\`\`\` *${transport_service.from_to}*${
				showPickup(start_date, start_time_local, start_date_local) &&
				!has_cab_time_variation
					? ` *(Pickup at ${formatDate(start_time_local ? parseDate(start_time_local, "HH:mm:ss") : utcTimestampToLocalDate(start_date), "HH:mm [hrs]")})*`
					: ``
			} - ${transport_service.service_display_name}${
				!has_cab_time_variation
					? renderWhatsAppLocation({
							use_hotel_as_pickup: cab_schedule.use_hotel_as_pickup,
							use_hotel_as_drop: cab_schedule.use_hotel_as_drop,
							checkinHotels,
							checkoutHotels,
							stayHotels,
							pick_up_location: cab_schedule.pick_up_location,
							drop_location: cab_schedule.drop_location,
							prefix: " :",
						})
					: ""
			}${
				(remarks || customer_remarks) && includeRemarks
					? `
\`\`\`•\`\`\` *Remarks*
${(remarks || customer_remarks || "")
	.split("\n")
	.map((c) => `\`\`\` |\`\`\` ${c}`)
	.join("\n")}`
					: ``
			}${includeItinerary && itinerary?.description ? `\n${itinerary.description}` : ``}

${cabs
	.map(
		({
			driver,
			cab,
			cab_type,
			confirmation_details,
			remarks,
			customer_remarks,
			tourists,
			pickup_time_local,
			use_hotel_as_pickup,
			use_hotel_as_drop,
			pick_up_location,
			drop_location,
		}) => {
			const guests = formatTouristInfoForWhatsapp(tourists)
			return `\`\`\`-\`\`\` *${driver?.name || "Missing Driver Details"}*${
				driver?.phone_number ? ` (${driver.phone_number})` : ``
			}
\`\`\` -\`\`\`  ${
				cab_type?.display_name || cab
					? `*${
							cab ? `${cab.name} - ${cab.number_plate}` : cab_type.display_name
						}*`
					: ""
			} ${
				pickup_time_local && has_cab_time_variation
					? `*(Pickup at ${formatDate(parseDate(pickup_time_local, "HH:mm:ss"), "HH:mm [hrs]")})*`
					: ""
			}${
				has_cab_time_variation
					? renderWhatsAppLocation({
							use_hotel_as_pickup,
							use_hotel_as_drop,
							checkinHotels,
							checkoutHotels,
							stayHotels,
							pick_up_location,
							drop_location,
							prefix: " :",
						})
					: ""
			} ${
				confirmation_details
					? `
${confirmation_details
	.split("\n")
	.map((c) => `\`\`\` |\`\`\` ${c}`)
	.join("\n")}
${
	(remarks || customer_remarks) && includeRemarks
		? `\`\`\` •\`\`\` *Remarks*\n${(remarks || customer_remarks || "")
				.split("\n")
				.map((c) => `\`\`\` |\`\`\` ${c}`)
				.join("\n")}`
		: ``
}
`
					: ``
			}${includeAllTourist && guests ? `\n\`\`\` -\`\`\` *Guests:* ${guests}` : ""}`
		}
	)
	.join("\n")}`
		}
		if (travel_activity_booking) {
			const {
				activity,
				ticket_type,
				supplier_contact,
				ticket_tourist_configurations_full_name,
				start_time_formatted,
				duration_formatted,
				confirmation_details,
				remarks,
				itinerary,
				tourists,
				use_hotel_as_pickup,
				use_hotel_as_drop,
				pick_up_location,
				drop_location,
			} = travel_activity_booking

			const guests = formatTouristInfoForWhatsapp(tourists)
			return `\`\`\`-\`\`\` *${activity.name}${ticket_type ? ` - ${ticket_type.display_name}` : ""}*${
				start_time_formatted || duration_formatted
					? `
\`\`\` :\`\`\` *${[start_time_formatted, duration_formatted].filter(Boolean).join(" - ")}*`
					: ``
			}${renderWhatsAppLocation({
				use_hotel_as_pickup,
				use_hotel_as_drop,
				checkinHotels,
				checkoutHotels,
				stayHotels,
				pick_up_location,
				drop_location,
				prefix: " :",
			})}
\`\`\` :\`\`\` *${ticket_tourist_configurations_full_name}*${
				confirmation_details
					? `
${confirmation_details
	.split("\n")
	.map((c) => `\`\`\` |\`\`\` ${c}`)
	.join("\n")}`
					: ``
			}${
				remarks && includeRemarks
					? `
\`\`\` •\`\`\` *Remarks*
${remarks
	.split("\n")
	.map((c) => `\`\`\` |\`\`\` ${c}`)
	.join("\n")}`
					: ``
			}${
				supplier_contact
					? `
\`\`\` :\`\`\` *Contact Person*
\`\`\`  \`\`\` ${supplier_contact.name} (${supplier_contact.phone_number})`
					: ``
			}${includeItinerary && itinerary?.description ? `\n${itinerary.description}` : ``}${includeAllTourist && guests ? `\n\`\`\` :\`\`\` *Guests:* ${guests}` : ``}`
		}
		return ``
	})
	.filter(Boolean)
	.join("\n\n")}${
				whatsappForHotels
					? `

${whatsappForHotels}
`
					: ``
			}${
				departure_details?.length
					? `

*Departure Details:*
${departure_details
	.map(
		(d) =>
			`\`\`\`- ${formatDate(
				localOrUtcTimestampToLocalDate(d.date_time_local, d.date_time),
				"ddd DD MMM [at] HH:mm [hrs]"
			)}\`\`\`\n_${d.description}_`
	)
	.join("\n")}`
					: ``
			}`
		}
	)
	.join(`\n\`\`\`------------\`\`\`\n\n`)}
\`\`\`------------\`\`\`
_For any queries, please contact the trip coordinator._`
			return messagePerTrip
		},
		{}
	)
}

function getWhatsAppMessageContentForDrivers(
	bookings: Array<TTripOperationalBooking>,
	{
		itinerary: includeItinerary,
		remarks: includeRemarks,
		primaryTourist: includePrimaryTourists,
		allTourist: includeAllTourist,
	}: Omit<TInclusionOptions, "prices" | "hotels">
) {
	const drivers: Array<TDriver> = []
	const schedules = bookings.flatMap(({ daywise_services, ...t }) =>
		(daywise_services || []).flatMap(({ services, ...others }) =>
			services
				.map((s) => s.cab_schedule)
				.filter((s): s is ICabSchedule => Boolean(s))
				.map((schedule) => ({
					...others,
					...schedule,
					trip: t,
				}))
		)
	)
	schedules.forEach(function (s) {
		s.cabs.forEach((cab) => {
			const { driver } = cab
			if (driver && drivers.findIndex((d) => d.id === driver.id) === -1) {
				drivers.push(driver)
			}
		})
	})
	const schedulesPerDriver = drivers.map((driver) => {
		return {
			driver,
			schedules: schedules
				.map((s) => ({
					...s,
					cabs: s.cabs.filter((cab) => cab.driver?.id === driver.id),
				}))
				.filter((s) => s.cabs.length),
		}
	})
	return schedulesPerDriver
		.map(function getMessagePerDriver({
			driver,
			schedules,
		}): [driver: TDriver, message: string] {
			const groupedByTrip = collect(schedules)
				.groupBy((s) => s.trip_id)
				.toArray()
			return [
				driver,
				`Dear ${driver.name},
Here are the details of your upcoming ${pluralize("service", schedules.length)}.

${groupedByTrip
	.map((schedules) => {
		const schedule = schedules[0]
		const { trip } = schedule
		const hasSameCabsForAll =
			collect(
				schedules
					.map((s) => {
						const { cabs } = s
						return cabs
							.reduce<Array<string>>((cabs, cab) => {
								const remarksSuffix = cab.remarks ? `:R${cab.remarks}` : ""
								const supplierRemarksSuffix = cab.supplier_remarks
									? `:SR${cab.supplier_remarks}`
									: ""
								if (cab.cab) {
									cabs.push(
										`${cab.cab.id}C${remarksSuffix}C${supplierRemarksSuffix}`
									)
								} else {
									cabs.push(
										`${cab.cab_type.id}CT${remarksSuffix}CT${supplierRemarksSuffix}`
									)
								}
								return cabs
							}, [])
							.filter(Boolean)
							.sort()
							.join("_")
					})
					.filter(Boolean)
			)
				.unique()
				.count() === 1
		const sameCabsForAll = schedule.cabs
		const guestName = trip.tourist?.name || "N/A"

		return `\`\`\`-----------\`\`\`
\`\`\`Trip Id :\`\`\` ${trip.branded_id}${
			!includePrimaryTourists
				? ""
				: `
\`\`\`Guest   :\`\`\` *${guestName}*${
						trip.tourist?.phone_numbers?.length
							? `
\`\`\`        |\`\`\` ${trip.tourist.phone_numbers
									.map((p) => p.phone_number)
									.join(", ")}`
							: ``
					}`
		}
\`\`\`Pax     :\`\`\` *${pluralize("Adult", trip.no_of_adults, true)}${
			trip.children.length
				? ` with ${pluralize(
						"Child",
						trip.children.length,
						true
					)} (${trip.children.map((c) => `${c.age}y`).join(",")})`
				: ``
		}*${
			hasSameCabsForAll
				? `\n\`\`\`Cab     :\n\`\`\`${sameCabsForAll
						.map(({ cab, cab_type, remarks, supplier_remarks, tourists }) => {
							const guests = formatTouristInfoForWhatsapp(tourists)
							return `\`\`\`-\`\`\` *${cab?.full_name || cab_type.name.trim()}*${(remarks || supplier_remarks) && includeRemarks ? `\n\`\`\`:\`\`\` *Remarks:* ${remarks || supplier_remarks}` : ``}${
								includeAllTourist && guests
									? `\n\`\`\` |\`\`\` *Guests:* ${guests}`
									: ``
							}\n`
						})
						.join("\n")}`
				: ``
		}
${schedules
	.map((schedule, index, schedules) => {
		const {
			cabs,
			start_date,
			start_time_local,
			start_date_local,
			transport_service,
			checkin_hotels,
			checkout_hotels,
			stay_hotels,
			arrival_details,
			departure_details,
			remarks,
			supplier_remarks,
			itinerary,
			self_booked_checkin_hotels,
			self_booked_checkout_hotels,
			self_booked_stay_hotels,
			has_cab_time_variation,
		} = schedule
		const guests = formatTouristInfoForWhatsapp(cabs[0].tourists)
		const { checkinHotels, stayHotels, checkoutHotels } = combineBookings({
			checkout_hotels,
			checkin_hotels,
			stay_hotels,
			self_booked_checkout_hotels,
			self_booked_checkin_hotels,
			self_booked_stay_hotels,
			showSelfBooked: true,
		})
		const whatsappForHotels = getWhatsAppContentForHotels(
			{
				checkout_hotels,
				checkin_hotels,
				stay_hotels,
				self_booked_checkout_hotels,
				self_booked_checkin_hotels,
				self_booked_stay_hotels,
			},
			index > 0 ? schedules[index - 1] : undefined
		)
		const singleCab = cabs.length === 1
		const multipleCabs = cabs.length > 1

		const servicePickupTime =
			showPickup(start_date, start_time_local, start_date_local) &&
			(!has_cab_time_variation || singleCab)
				? ` *(Pickup at ${formatDate(start_time_local ? parseDate(start_time_local, "HH:mm:ss") : utcTimestampToLocalDate(start_date), "HH:mm [hrs]")})*`
				: ""
		return `---------------------------------
*${formatDate(localOrUtcTimestampToLocalDate(start_date_local, start_date), "ddd DD MMM, YYYY")}*${servicePickupTime}${
			arrival_details?.length
				? `

*Guest Arrival Details:*
${arrival_details
	.map(
		(d) =>
			`\`\`\`- ${formatDate(
				localOrUtcTimestampToLocalDate(d.date_time_local, d.date_time),
				"ddd DD MMM [at] HH:mm [hrs]"
			)}\`\`\`\n_${d.description}_`
	)
	.join("\n\n")}`
				: ``
		}
\`\`\`-\`\`\` *${transport_service.from_to}*
\`\`\` |\`\`\` ${transport_service.service_display_name}${
			!hasSameCabsForAll && includeAllTourist && guests
				? `\n\`\`\` |\`\`\` *Guests:* ${guests}`
				: ``
		}${
			!has_cab_time_variation || singleCab
				? renderWhatsAppLocation({
						pick_up_location: !has_cab_time_variation
							? schedule.pick_up_location
							: cabs[0].pick_up_location,
						drop_location: !has_cab_time_variation
							? schedule.drop_location
							: cabs[0].drop_location,
						use_hotel_as_pickup: !has_cab_time_variation
							? schedule.use_hotel_as_pickup
							: cabs[0].use_hotel_as_pickup,
						use_hotel_as_drop: !has_cab_time_variation
							? schedule.use_hotel_as_drop
							: cabs[0].use_hotel_as_drop,
						checkinHotels,
						checkoutHotels,
						stayHotels,
					})
				: ""
		}${
			(remarks || supplier_remarks) && includeRemarks
				? `
\`\`\` •\`\`\` *Remarks*
${(remarks || supplier_remarks || "")
	.split("\n")
	.map((c) => `\`\`\` |\`\`\` ${c}`)
	.join("\n")}`
				: ``
		}${
			!hasSameCabsForAll || (has_cab_time_variation && multipleCabs)
				? `
\`\`\` *Cab:*\`\`\` ${cabs.map((cab) => {
						const locationDisplay =
							has_cab_time_variation && multipleCabs
								? renderWhatsAppLocation({
										pick_up_location: cab.pick_up_location,
										drop_location: cab.drop_location,
										use_hotel_as_pickup: cab.use_hotel_as_pickup,
										use_hotel_as_drop: cab.use_hotel_as_drop,
										checkinHotels,
										checkoutHotels,
										stayHotels,
										prefix: "  :",
									})
								: ``
						const pickupTime =
							cab.pickup_time_local && has_cab_time_variation && multipleCabs
								? ` *(Pickup at ${formatDate(parseDate(cab.pickup_time_local, "HH:mm:ss"), "HH:mm [hrs]")})*`
								: ""
						return `\n*- ${cab.cab?.full_name || cab.cab_type.name}*${pickupTime}${locationDisplay}${
							(cab.remarks || cab.supplier_remarks) && includeRemarks
								? `\n\`\`\` -\`\`\` Remarks: ${cab.remarks || cab.supplier_remarks}`
								: ``
						}`
					})}`
				: ``
		}${
			includeItinerary && itinerary?.description
				? `\n${itinerary.description}`
				: ``
		}${
			whatsappForHotels
				? `

${whatsappForHotels}`
				: ``
		}${
			departure_details?.length
				? `

*Guest Departure Details:*
${departure_details
	.map(
		(d) =>
			`\`\`\`- ${formatDate(
				localOrUtcTimestampToLocalDate(d.date_time_local, d.date_time),
				"ddd DD MMM [at] HH:mm [hrs]"
			)}\`\`\`\n_${d.description}_`
	)
	.join("\n")}`
				: ``
		}`
	})
	.join("\n\n")}`
	})
	.join("\n\n")}`,
			]
		})
		.reduce<{ [driverId: string]: string }>(
			(messageByDriver, [driver, message]) => {
				messageByDriver[driver.id] = message
				return messageByDriver
			},
			{}
		)
}

function getTransportServiceProvidersWithBookings(
	bookings: Array<TTripOperationalBooking>
) {
	const transportServiceProviders: Array<ISupplier> = []
	bookings.forEach(function ({ daywise_services }) {
		daywise_services.forEach(({ services }) => {
			services.forEach(({ cab_schedule, travel_activity_booking }) => {
				if (cab_schedule) {
					cab_schedule.cabs.forEach((cab) => {
						const { transport_service_provider } = cab
						if (
							transport_service_provider &&
							transportServiceProviders.findIndex(
								(d) => d.id === transport_service_provider.id
							) === -1
						) {
							transportServiceProviders.push(transport_service_provider)
						}
					})
				}
				if (travel_activity_booking) {
					const { supplier } = travel_activity_booking
					if (
						supplier &&
						transportServiceProviders.findIndex((d) => d.id === supplier.id) ===
							-1
					) {
						transportServiceProviders.push(supplier)
					}
				}
			})
		})
	})

	const schedulesPerTsp = transportServiceProviders.map((tsp) => {
		return {
			transport_service_provider: tsp,
			bookings: bookings
				.map(({ cab_schedules, travel_activity_bookings, ...b }) => ({
					...b,
					daywise_services: b.daywise_services
						.map((d) => ({
							...d,
							services: d.services
								.map((s) => ({
									...s,
									cab_schedule: !s.cab_schedule
										? undefined
										: {
												...s.cab_schedule,
												cabs: s.cab_schedule.cabs.filter(
													(s) => s.transport_service_provider?.id === tsp.id
												),
											},
									travel_activity_booking:
										s.travel_activity_booking &&
										s.travel_activity_booking.supplier?.id === tsp.id
											? s.travel_activity_booking
											: undefined,
								}))
								.filter(
									(s) =>
										s.cab_schedule?.cabs?.length || s.travel_activity_booking
								),
						}))
						.filter((d) => d.services?.length),
				}))
				.filter((b) => b.daywise_services?.length),
		}
	})

	const schedulesByTspId = schedulesPerTsp.reduce<{
		[key: string]: (typeof schedulesPerTsp)[number]["bookings"]
	}>((byId, { transport_service_provider, bookings }) => {
		const id = transport_service_provider.id
		if (!byId[id]) {
			byId[id] = []
		}
		byId[id] = byId[id].concat(bookings)
		return byId
	}, {})
	return { transportServiceProviders, schedulesPerTsp, schedulesByTspId }
}

function getWhatsAppMessageContentForTransportServiceProviders(
	bookings: Array<TTripOperationalBooking>,
	{
		prices: includePrices,
		itinerary: includeItinerary,
		remarks: includeRemarks,
		primaryTourist: includePrimaryTourists,
		allTourist: includeAllTourist,
		hotels: includeHotels,
	}: TInclusionOptions
) {
	const { schedulesPerTsp } = getTransportServiceProvidersWithBookings(bookings)
	return schedulesPerTsp
		.map(function getMessagePerTsp({
			transport_service_provider,
			bookings,
		}): [tsp: ISupplier, message: string] {
			let totalPrice: Array<TMoney> = []
			return [
				transport_service_provider,
				`Hi ${transport_service_provider.name},
Here are the details of your upcoming ${pluralize("service", bookings.length)}.

${bookings
	.map(({ daywise_services, ...trip }) => {
		const cab_schedules = daywise_services
			.flatMap((d) => d.services)
			.map((s) => s.cab_schedule)
			.filter((s): s is Exclude<typeof s, undefined> => Boolean(s))
		const hasActivities = daywise_services.some((d) =>
			d.services.some((s) => s.travel_activity_booking)
		)
		const schedule = cab_schedules[0]
		const hasSameCabsForAll =
			!hasActivities && getHasSameCabDriverForSchedules(cab_schedules)
		// const hasSameGuestForAll = '';
		const sameCabsForAll = schedule?.cabs || []
		const hasDriverOrCabAssigned = sameCabsForAll.some((s) => s.driver || s.cab)
		let cabTypeWithCount: Array<{ count: number; name: string }> = []
		let consolidatedHeaderCabs: Array<TConsolidatedCab> = []
		if (hasSameCabsForAll && !hasDriverOrCabAssigned) {
			const cabsCountByName = sameCabsForAll.reduce<{
				[cab: string]: number
			}>((cabTypeWithCount, cab) => {
				const key = cab.cab_type.name
				if (!cabTypeWithCount[key]) {
					cabTypeWithCount[key] = 0
				}
				cabTypeWithCount[key] += 1
				return cabTypeWithCount
			}, {})
			cabTypeWithCount = Object.keys(cabsCountByName).map((name) => ({
				name,
				count: cabsCountByName[name],
			}))
			consolidatedHeaderCabs = consolidateCabsByPickup(sameCabsForAll)
		}
		const guestName = trip.tourist?.name || "N/A"
		return `\`\`\`-----------\`\`\`
\`\`\`Trip Id :\`\`\`${trip.branded_id}${
			!includePrimaryTourists
				? ``
				: `
\`\`\`Guest   :\`\`\` *${guestName}*${
						trip.tourist?.phone_numbers?.length
							? `
\`\`\`        |\`\`\` ${trip.tourist.phone_numbers
									.map((p) => p.phone_number)
									.join(", ")}`
							: ``
					}`
		}
\`\`\`Pax     :\`\`\` *${pluralize("Adult", trip.no_of_adults, true)}${
			trip.children.length
				? ` with ${pluralize(
						"Child",
						trip.children.length,
						true
					)} (${trip.children.map((c) => `${c.age}y`).join(",")})`
				: ``
		}*${
			hasSameCabsForAll
				? `\n----\n*${hasDriverOrCabAssigned ? "Driver/Cab" : "Cabs"}*${
						hasDriverOrCabAssigned
							? sameCabsForAll
									.map(
										({
											cab,
											driver,
											cab_type,
											remarks,
											supplier_remarks,
											tourists,
										}) => {
											const guests = formatTouristInfoForWhatsapp(tourists)
											return `\n\`\`\`-\`\`\` *${cab?.full_name || cab_type.name.trim()}*${
												!driver
													? ``
													: `
\`\`\`:\`\`\` *${driver?.name}*${driver?.phone_number ? ` (${driver.phone_number})` : ``}`
											} ${(remarks || supplier_remarks) && includeRemarks ? `\n\`\`\`:\`\`\` *Remarks :* ${remarks || supplier_remarks}` : ``}${
												includeAllTourist && guests
													? `\n\`\`\`:\`\`\`*Guests:* ${guests}`
													: ``
											}`
										}
									)
									.join(`\n`)
							: consolidatedHeaderCabs.length
								? consolidatedHeaderCabs
										.map((cab) => {
											const remarksText =
												includeRemarks && (cab.remarks || cab.supplier_remarks)
													? `\n\`\`\`  |\`\`\` *Remarks:* ${cab.remarks || cab.supplier_remarks}`
													: ""
											const guests = formatTouristInfoForWhatsapp(cab.tourists)
											return `\n\`\`\`-\`\`\` *${cab.count}-${cab.cab_type.name.trim()}*${remarksText}${
												includeAllTourist && guests
													? `\n\`\`\`-\`\`\`*Guests:* ${guests}`
													: ``
											}`
										})
										.join("\n")
								: `\`\`\`- \`\`\` ${cabTypeWithCount
										.map((c) => `${c.count}-${c.name}`)
										.join(" • ")}`
					}`
				: ``
		}

${daywise_services
	.map((schedule, index, schedules) => {
		const {
			date,
			date_local,
			services,
			checkin_hotels,
			checkout_hotels,
			stay_hotels,
			arrival_details,
			departure_details,
			self_booked_checkin_hotels,
			self_booked_checkout_hotels,
			self_booked_stay_hotels,
		} = schedule
		const { checkinHotels, stayHotels, checkoutHotels } = combineBookings({
			checkout_hotels,
			checkin_hotels,
			stay_hotels,
			self_booked_checkout_hotels,
			self_booked_checkin_hotels,
			self_booked_stay_hotels,
			showSelfBooked: true,
		})
		const whatsappForHotels = getWhatsAppContentForHotels(
			{
				checkin_hotels,
				checkout_hotels,
				stay_hotels,
				self_booked_checkin_hotels,
				self_booked_checkout_hotels,
				self_booked_stay_hotels,
			},
			index > 0 ? schedules[index - 1] : undefined
		)
		return `---------------------------------
🗓️ *${formatDate(localOrUtcTimestampToLocalDate(date_local, date), "dddd DD MMM, YYYY")}*${
			arrival_details?.length
				? `
*Guest Arrival Details:*
${arrival_details
	.map(
		(d) =>
			`\`\`\`- ${formatDate(
				localOrUtcTimestampToLocalDate(d.date_time_local, d.date_time),
				"ddd DD MMM [at] HH:mm [hrs]"
			)}\`\`\`\n_${d.description}_`
	)
	.join("\n")}`
				: ``
		}

${services
	.map(({ cab_schedule, travel_activity_booking }) => {
		if (cab_schedule) {
			const {
				cabs,
				start_date,
				start_date_local,
				start_time_local,
				transport_service,
				remarks,
				supplier_remarks,
				itinerary,
				has_cab_time_variation,
				tourists,
			} = cab_schedule
			const guests = formatTouristInfoForWhatsapp(tourists)
			const price = getBookingPriceForCabs(cabs)
			totalPrice = addMoneyFromDifferentCurrencies(totalPrice.concat(price))
			const singleCab = cabs.length === 1
			const multipleCabs = cabs.length > 1
			const serviceLocationText =
				!has_cab_time_variation || singleCab
					? renderWhatsAppLocation({
							pick_up_location: !has_cab_time_variation
								? cab_schedule.pick_up_location
								: cabs[0].pick_up_location,
							drop_location: !has_cab_time_variation
								? cab_schedule.drop_location
								: cabs[0].drop_location,
							use_hotel_as_pickup: !has_cab_time_variation
								? cab_schedule.use_hotel_as_pickup
								: cabs[0].use_hotel_as_pickup,
							use_hotel_as_drop: !has_cab_time_variation
								? cab_schedule.use_hotel_as_drop
								: cabs[0].use_hotel_as_drop,
							checkinHotels,
							checkoutHotels,
							stayHotels,
						})
					: ""
			const servicePickupTimeText =
				showPickup(start_date, start_time_local, start_date_local) &&
				(!has_cab_time_variation || singleCab)
					? ` *(Pickup at ${formatDate(start_time_local ? parseDate(start_time_local, "HH:mm:ss") : utcTimestampToLocalDate(start_date), "HH:mm [hrs]")})*`
					: ``
			const shouldShowCabDetails =
				!hasSameCabsForAll || (has_cab_time_variation && multipleCabs)
			const hasMultipleCabVariation = has_cab_time_variation && multipleCabs
			return `\`\`\`-\`\`\` *${transport_service.from_to}*${servicePickupTimeText}${serviceLocationText}
\`\`\` |\`\`\` ${transport_service.service_display_name}${
				(remarks || supplier_remarks) && includeRemarks
					? `
\`\`\` •\`\`\` *Remarks*
${(remarks || supplier_remarks || "")
	.split("\n")
	.map((c: string) => `\`\`\` |\`\`\` ${c}`)
	.join("\n")}`
					: ``
			}${includeAllTourist && guests ? `\n\`\`\` |\`\`\` *Guests:* ${guests}` : ``}${
				includePrices && price.length
					? `
\`\`\` -\`\`\` *${price.map((p) => formatMoneyByIntl(p, { showCurrency: true, showSolidus: true })).join(" + ")}*`
					: ``
			}${
				shouldShowCabDetails
					? `
\`\`\`*Cabs*\n\`\`\`${cabs
							.map((cab) => {
								const guests = formatTouristInfoForWhatsapp(cab.tourists)
								const pickupTimeText =
									cab.pickup_time_local && hasMultipleCabVariation
										? `*(Pickup at ${formatDate(parseDate(cab.pickup_time_local, "HH:mm:ss"), "HH:mm [hrs]")})*`
										: ""
								const cabsLocationText = hasMultipleCabVariation
									? renderWhatsAppLocation({
											use_hotel_as_pickup: cab.use_hotel_as_pickup,
											use_hotel_as_drop: cab.use_hotel_as_drop,
											checkinHotels,
											checkoutHotels,
											stayHotels,
											pick_up_location: cab.pick_up_location,
											drop_location: cab.drop_location,
											prefix: " :",
										})
									: ``
								return [
									`*: ${cab.cab?.full_name || cab.cab_type.name}* ${pickupTimeText}${cabsLocationText}`,
									cab.driver && !hasSameCabsForAll
										? `*${cab.driver.name}* ${cab.driver.phone_number ? `(${cab.driver.phone_number})` : ""}`
										: ``,
									(cab.remarks || cab.supplier_remarks) && includeRemarks
										? `${`*Remarks:* ${cab.remarks || cab.supplier_remarks}`}`
										: ``,
									includeAllTourist && guests ? `*Guests:* ${guests}` : ``,
								]
									.filter(Boolean)
									.join(`\n\`\`\`- \`\`\``)
							})
							.join(`\n\n`)}`
					: ``
			}${includeItinerary && itinerary?.description ? `\n${itinerary.description}` : ``}`
		}
		if (travel_activity_booking) {
			const {
				activity,
				ticket_type,
				ticket_tourist_configurations_full_name_with_complementary,
				start_time_formatted,
				duration_formatted,
				confirmation_details,
				remarks,
				itinerary,
				tourists,
				booked_price,
				currency,
				use_hotel_as_pickup,
				use_hotel_as_drop,
				pick_up_location,
				drop_location,
			} = travel_activity_booking
			const price = moneyParseByDecimal(Number(booked_price || 0), currency)
			totalPrice = addMoneyFromDifferentCurrencies(totalPrice.concat([price]))
			const guests = formatTouristInfoForWhatsapp(tourists)
			// const primaryGuest = tourist
			return `\`\`\`-\`\`\` *${activity.name}${ticket_type ? ` - ${ticket_type.display_name}` : ""}*${
				start_time_formatted || duration_formatted
					? ` *(${[start_time_formatted, duration_formatted].filter(Boolean).join(" - ")})*`
					: ``
			}${renderWhatsAppLocation({
				use_hotel_as_pickup,
				use_hotel_as_drop,
				checkinHotels,
				checkoutHotels,
				stayHotels,
				pick_up_location,
				drop_location,
				prefix: " :",
			})}
\`\`\` :\`\`\` *${ticket_tourist_configurations_full_name_with_complementary}*${
				confirmation_details
					? `
${confirmation_details
	.split("\n")
	.map((c) => `\`\`\` |\`\`\` ${c}`)
	.join("\n")}`
					: ``
			}${includeAllTourist && guests ? `\n\`\`\` :\`\`\` *Guests:* ${guests}` : ``}${
				remarks && includeRemarks
					? `
\`\`\` •\`\`\` *Remarks*
${remarks
	.split("\n")
	.map((c) => `\`\`\` |\`\`\` ${c}`)
	.join("\n")}`
					: ``
			}${
				includePrices && price
					? `
\`\`\` -\`\`\` *${formatMoneyByIntl(price, { showCurrency: true, showSolidus: true })}*`
					: ``
			}${includeItinerary && itinerary?.description ? `\n${itinerary.description}` : ``}`
		}
		return ``
	})
	.join("\n\n")}${
			includeHotels && whatsappForHotels
				? `

${whatsappForHotels}
`
				: ``
		}${
			departure_details?.length
				? `

*Guest Departure Details:*
${departure_details
	.map(
		(d) =>
			`\`\`\`- ${formatDate(
				localOrUtcTimestampToLocalDate(d.date_time_local, d.date_time),
				"ddd DD MMM [at] HH:mm [hrs]"
			)}\`\`\`\n_${d.description}_`
	)
	.join("\n")}`
				: ``
		}`
	})
	.join("\n\n")}`
	})
	.join("\n\n")}${
					includePrices && totalPrice.length
						? `

*Total Price: ${totalPrice.map((p) => formatMoneyByIntl(p, { showCurrency: true, showSolidus: true })).join(" + ")}*`
						: ``
				}
`,
			]
		})
		.reduce<{ [tspId: string]: string }>((messageByTsp, [tsp, message]) => {
			messageByTsp[tsp.id] = message
			return messageByTsp
		}, {})
}

function TransportServiceProviderEmailContacts({
	selectedTsp,
	emailSubject,
}: {
	selectedTsp?: ISupplier
	emailSubject?: string
}) {
	const isWindowsOs = useMemo(() => {
		try {
			if (navigator.userAgent.indexOf("Win") !== -1) {
				return true
			}
		} catch (e) {
			// Ignore
		}
		return false
	}, [])
	if (!selectedTsp) return null

	const tspContacts = selectedTsp.contacts || []
	return (
		<Inline
			alignItems={"center"}
			padding="4"
			bgColor="subtle"
			borderBottomWidth="1"
			gap="3"
		>
			<Text color="primary" fontWeight="semibold" fontSize="sm">
				<Link
					color="accent"
					to={generatePath("/suppliers/:supplierId", {
						supplierId: String(selectedTsp.id),
					})}
				>
					Supplier Contacts <Icons.ChevronDown rotate="270" />
				</Link>
			</Text>
			<Inline gap="6" flexWrap="nowrap" overflow="auto">
				{tspContacts
					.filter((contact): contact is Required<typeof contact, "email"> =>
						Boolean(contact.email)
					)
					.map((contact, index, allContacts) => {
						const { phone_number, email, name, id } = contact
						return (
							<Stack gap="px" key={id}>
								<Box fontWeight="semibold">
									{name} <PhoneNumber value={phone_number} iconOnly />
								</Box>
								{emailSubject ? (
									<CopyToClipboard>
										{({ copyTextToClipboard }) => (
											<Inline gap="2" alignItems="center">
												<CopyToClipboardButton
													size="sm"
													inline
													onClick={() => copyTextToClipboard(email)}
													level="tertiary"
													iconOnly
												>
													<Icons.ClipboardCopy title="Copy to Clipboard" />
												</CopyToClipboardButton>
												<Box paddingY="1">
													<Text title={email}>
														{email.split("@")[0]}
														<Text as="span" color="muted">
															@
														</Text>
													</Text>
												</Box>
												<Button
													as="a"
													size="sm"
													href={`mailto:${email}?subject=${encodeURIComponent(
														emailSubject
													)}&cc=${allContacts
														.filter((_c, i) => i !== index)
														.map((c) => c.email)
														.join(isWindowsOs ? ";" : ",")}`}
													title="Open Email Client with subject auto-filled"
												>
													<Icons.ArrowTopRightOnSquare />
												</Button>
											</Inline>
										)}
									</CopyToClipboard>
								) : null}
							</Stack>
						)
					})}
			</Inline>
		</Inline>
	)
}

function EmailContentForTransportServiceProviders({
	bookings,
	selectedTsp,
	includePrices,
	includeItinerary,
	includeRemarks,
	includePrimaryTourists,
	includeAllTourist,
	includeHotels,
}: {
	bookings: Array<TTripOperationalBooking>
	selectedTsp?: ISupplier
	includePrices: boolean
	includeItinerary: boolean
	includeRemarks: boolean
	includePrimaryTourists: boolean
	includeAllTourist: boolean
	includeHotels: boolean
}) {
	const schedulesByTspId = useMemo(() => {
		return getTransportServiceProvidersWithBookings(bookings).schedulesByTspId
	}, [bookings])

	const emailSubjectLine = useMemo(() => {
		const hasActivity = Boolean(
			bookings.flatMap((b) =>
				b.travel_activity_bookings
					?.flatMap((tab) => tab.supplier)
					.filter((tab) => tab?.name === selectedTsp?.name)
			).length > 0
		)
		const hasTransport = Boolean(
			bookings.flatMap((b) =>
				b.cab_schedules?.flatMap((cs) =>
					cs.cabs
						.flatMap((c) => c.transport_service_provider)
						.filter((cs) => cs?.name === selectedTsp?.name)
				)
			).length > 0
		)

		if (hasActivity && !hasTransport) {
			return "Activity Service Details"
		} else if (!hasActivity && hasTransport) {
			return "Transport Service Details"
		} else if (hasActivity && hasTransport) {
			return "Transport and Activity Service Details"
		}
		return "Service Details"
	}, [bookings, selectedTsp])

	if (!selectedTsp) return null
	const schedulesForSelectedTsp = schedulesByTspId[selectedTsp.id]
	const schedules = schedulesForSelectedTsp
	let totalPrice: Array<TMoney> = []
	const tenantBrand = schedulesForSelectedTsp[0]?.tenant_brand
	// Dynamically determine the email subject line based on presence of activities/transport
	const emailSubject = `Trip ID : ${collect(schedules.map((d) => d.branded_id))
		.unique()
		.toArray()
		.join(
			"_"
		)}:: ${emailSubjectLine} from ${tenantBrand?.short_name} to ${selectedTsp.name}`
	return (
		<EmailShare
			shareWith="transport_service_provider"
			subject={
				<>
					<Text>{emailSubject}</Text>
				</>
			}
			headerSection={
				<TransportServiceProviderEmailContacts
					selectedTsp={selectedTsp}
					emailSubject={emailSubject}
				/>
			}
		>
			<>
				<Box as="p">Hi {selectedTsp.name},</Box>
				{!tenantBrand ? null : (
					<>
						<br />
						<Box as="p">
							Greetings from{" "}
							<b>
								{tenantBrand.short_name}
								{tenantBrand.name
									.toLowerCase()
									.indexOf(tenantBrand.short_name.toLowerCase()) === -1
									? ` (${tenantBrand.name})`
									: null}
							</b>
							.
						</Box>
					</>
				)}
				<br />
				<Box as="p">
					Here are the details of your upcoming{" "}
					{pluralize("Trips", schedules.length)}.
				</Box>
				<br />
				<table style={{ width: "100%" }}>
					{schedules.map(({ daywise_services, ...trip }, index) => {
						const cab_schedules = daywise_services
							.flatMap((d) => d.services)
							.map((s) => s.cab_schedule)
							.filter((s): s is Exclude<typeof s, undefined> => Boolean(s))
						const hasActivities = daywise_services.some((d) =>
							d.services.some((d) => d.travel_activity_booking)
						)
						const schedule = cab_schedules[0]
						const hasSameCabsForAll =
							!hasActivities && getHasSameCabDriverForSchedules(cab_schedules)
						const sameCabsForAll = schedule?.cabs || []
						const hasDriverOrCabAssigned = sameCabsForAll.some(
							(s) => s.driver || s.cab
						)
						let cabTypeWithCount: Array<{ count: number; name: string }> = []
						let consolidatedHeaderCabs: Array<TConsolidatedCab> = []
						if (hasSameCabsForAll && !hasDriverOrCabAssigned) {
							const cabsCountByName = sameCabsForAll.reduce<{
								[cab: string]: number
							}>((cabTypeWithCount, cab) => {
								if (!cabTypeWithCount[cab.cab_type.name]) {
									cabTypeWithCount[cab.cab_type.name] = 0
								}
								cabTypeWithCount[cab.cab_type.name] += 1
								return cabTypeWithCount
							}, {})
							cabTypeWithCount = Object.keys(cabsCountByName).map((name) => ({
								name,
								count: cabsCountByName[name],
							}))

							consolidatedHeaderCabs = consolidateCabsByPickup(sameCabsForAll)
						}
						const cols = (hasSameCabsForAll ? 4 : 6) + (includePrices ? 1 : 0)
						const arrival_details: TArrivalDepartureDetail[] = []
						for (const dayDetails of daywise_services) {
							if (dayDetails.arrival_details) {
								dayDetails.arrival_details.forEach((s) => {
									if (!arrival_details.find((d) => d.id === s.id)) {
										arrival_details.push(s)
									}
								})
							}
						}
						const departure_details: TArrivalDepartureDetail[] = []
						for (const dayDetails of daywise_services) {
							if (dayDetails.departure_details) {
								dayDetails.departure_details.forEach((s) => {
									if (!departure_details.find((d) => d.id === s.id)) {
										departure_details.push(s)
									}
								})
							}
						}
						return (
							<tbody key={trip.id}>
								{index > 0 ? (
									<>
										<tr>
											<td colSpan={cols + 1}></td>
										</tr>
										<tr>
											<td colSpan={cols + 1}>+++++++++++++++++++++++++</td>
										</tr>
									</>
								) : null}
								<tr>
									<th>Trip Id</th>
									{!includePrimaryTourists ? null : (
										<>
											<th>Guest</th>
											<th>Contact No.</th>
										</>
									)}
									<th colSpan={cols - 2 + (!includePrimaryTourists ? 2 : 0)}>
										Pax
									</th>
								</tr>
								<tr>
									<td>{trip.branded_id}</td>
									{!includePrimaryTourists ? null : (
										<>
											<th>
												{trip.tourist ? (
													<>
														<p>{trip.tourist.name}</p>
														{trip.tourist.remarks && (
															<p
																style={{
																	fontWeight: "bold",
																	borderLeft: "2px solid silver",
																	paddingLeft: "4px",
																}}
															>
																<small
																	style={{
																		whiteSpace: "pre-line",
																	}}
																>
																	{trip.tourist.remarks}
																</small>
															</p>
														)}
													</>
												) : (
													"Name - To be updated"
												)}
											</th>
											<td>
												{trip.tourist?.phone_numbers?.length
													? trip.tourist.phone_numbers.map((p, idx) => (
															<Fragment key={p.phone_number}>
																{idx >= 1 ? <span>, </span> : null}
																<a href={`tel:${p.phone_number}`}>
																	{p.phone_number}
																</a>
															</Fragment>
														))
													: null}
											</td>
										</>
									)}
									<th colSpan={cols - 2 + (!includePrimaryTourists ? 2 : 0)}>
										{pluralize("Adult", trip.no_of_adults, true)}
										{trip.children.length
											? ` with ${pluralize(
													"Child",
													trip.children.length,
													true
												)} (${trip.children.map((c) => `${c.age}y`).join(",")})`
											: ``}
									</th>
								</tr>
								{arrival_details.length || departure_details.length ? (
									<>
										{arrival_details.map((d, index, arr) => (
											<tr key={d.id}>
												{index === 0 ? (
													<th rowSpan={arr.length}>Arrival Details</th>
												) : null}
												<td>
													{formatDate(
														localOrUtcTimestampToLocalDate(
															d.date_time_local,
															d.date_time
														),
														"ddd DD MMM [at] HH:mm [hrs]"
													)}
												</td>
												<td colSpan={cols - 1}>{d.description}</td>
											</tr>
										))}
										{departure_details.map((d, index, arr) => (
											<tr key={d.id}>
												{index === 0 ? (
													<th rowSpan={arr.length}>Departure Details</th>
												) : null}
												<td>
													{formatDate(
														localOrUtcTimestampToLocalDate(
															d.date_time_local,
															d.date_time
														),
														"ddd DD MMM [at] HH:mm [hrs]"
													)}
												</td>
												<td colSpan={cols - 1}>{d.description}</td>
											</tr>
										))}
									</>
								) : null}
								{hasSameCabsForAll ? (
									<>
										{!hasDriverOrCabAssigned ? (
											<tr>
												<th>Cab Details</th>
												<th colSpan={cols}>
													{consolidatedHeaderCabs.length
														? consolidatedHeaderCabs.map((cab, idx) => (
																<div key={idx}>
																	<b>
																		{cab.count}-{cab.cab_type.name}
																		{includeAllTourist &&
																		cab.tourists?.length ? (
																			<>
																				<br />
																				<p
																					style={{
																						fontWeight: "bold",
																						fontSize: "smaller",
																						color: "#555",
																					}}
																				>
																					<TouristInfoEmailView
																						tourists={cab.tourists}
																					/>
																				</p>
																			</>
																		) : null}
																	</b>
																	{(cab.remarks || cab.supplier_remarks) &&
																	includeRemarks ? (
																		<p
																			style={{
																				borderLeft: "2px solid silver",
																				paddingLeft: "4px",
																				marginTop: "4px",
																				marginBottom:
																					idx <
																					consolidatedHeaderCabs.length - 1
																						? "8px"
																						: "0",
																			}}
																		>
																			<b>Remarks : </b>
																			<Markdown className="remarks">
																				{cab.remarks || cab.supplier_remarks}
																			</Markdown>
																		</p>
																	) : null}
																</div>
															))
														: cabTypeWithCount
																.map((c) => `${c.count}-${c.name}`)
																.join(", ")}
												</th>
											</tr>
										) : (
											<>
												<tr>
													<td colSpan={cols + 1} style={{ paddingTop: "16px" }}>
														<small>Cab and Driver Details</small>
													</td>
												</tr>
												<tr>
													<th>Sr.</th>
													<th>Cab</th>
													<th>Driver</th>
													<th colSpan={cols - 2}>Contact No.</th>
												</tr>
												{sameCabsForAll.map(
													(
														{
															id,
															cab,
															driver,
															cab_type,
															remarks,
															supplier_remarks,
															tourists,
														},
														index
													) => (
														<tr key={id}>
															<td>{index + 1}.</td>
															<td>
																<b>
																	{cab?.name || cab_type.name}
																	{cab ? ` - ${cab?.number_plate}` : null}
																	{includeAllTourist &&
																	(tourists?.length || 0) ? (
																		<>
																			<br />
																			<p
																				style={{
																					fontWeight: "bold",
																					fontSize: "smaller",
																					color: "#555",
																				}}
																			>
																				<TouristInfoEmailView
																					tourists={tourists}
																				/>
																			</p>
																		</>
																	) : null}
																</b>
																{(remarks || supplier_remarks) &&
																includeRemarks ? (
																	<p
																		style={{
																			borderLeft: "2px solid silver",
																			paddingLeft: "4px",
																		}}
																	>
																		<b>Remarks : </b>
																		<Markdown className="remarks">
																			{remarks || supplier_remarks}
																		</Markdown>
																	</p>
																) : null}
															</td>
															<td>{driver?.name}</td>
															<td colSpan={cols - 2}>
																{driver?.phone_number ? (
																	<a href={`tel:${driver.phone_number}`}>
																		{driver.phone_number}
																	</a>
																) : null}
															</td>
														</tr>
													)
												)}
											</>
										)}
									</>
								) : null}
								<tr>
									<td colSpan={cols + 1} style={{ paddingTop: "16px" }}>
										<small>
											Here are the date-wise services
											{includeHotels &&
												" with check-in/check-out hotel details"}
										</small>
									</td>
								</tr>
								<tr>
									<th>Date</th>
									<th colSpan={hasSameCabsForAll ? (includeHotels ? 2 : 4) : 1}>
										Service
									</th>
									{!hasSameCabsForAll ||
									daywise_services.some((s) =>
										s.services.some(
											(service) => service.cab_schedule?.has_cab_time_variation
										)
									) ? (
										<>
											<th colSpan={includePrimaryTourists ? 2 : 1}>Supplies</th>
										</>
									) : null}
									<th>Pick-up/Drop</th>
									{includePrices ? (
										<th
											style={{ textAlign: "right" }}
											colSpan={includeHotels ? 1 : 2}
										>
											Price
										</th>
									) : null}
									{includeHotels ? (
										<>
											<th>Check-Out</th>
											<th>Check-In/Stay</th>
										</>
									) : null}
								</tr>
								{daywise_services.map((schedule, index) => {
									const {
										date,
										date_local,
										checkin_hotels,
										checkout_hotels,
										stay_hotels,
										self_booked_checkin_hotels,
										self_booked_checkout_hotels,
										self_booked_stay_hotels,
									} = schedule
									const rowsCount = schedule.services.reduce<number>(
										(totalRows, { cab_schedule, travel_activity_booking }) => {
											let cabRows = 0
											if (cab_schedule) {
												const { cabs = [], has_cab_time_variation } =
													cab_schedule

												cabRows =
													hasSameCabsForAll && !has_cab_time_variation
														? 1
														: has_cab_time_variation
															? cabs.length
															: consolidateCabsByPickup(cabs).length
											}
											return (
												totalRows + cabRows + (travel_activity_booking ? 1 : 0)
											)
										},
										0
									)
									const { checkinHotels, stayHotels, checkoutHotels } =
										combineBookings({
											checkout_hotels,
											checkin_hotels,
											stay_hotels,
											self_booked_checkout_hotels,
											self_booked_checkin_hotels,
											self_booked_stay_hotels,
										})

									return (
										<React.Fragment key={index}>
											{schedule.services.map(function (
												{ cab_schedule, travel_activity_booking },
												daywiseServiceIndex
											) {
												const dateCell =
													daywiseServiceIndex === 0 ? (
														<td rowSpan={rowsCount}>
															<Box
																as="time"
																whiteSpace="preserve"
																dateTime={localOrUtcTimestampToLocalDate(
																	date_local,
																	date
																).toISOString()}
															>
																<b>
																	{formatDate(
																		localOrUtcTimestampToLocalDate(
																			date_local,
																			date
																		),
																		"DD MMM"
																	)}
																</b>
																<br />
																<small>
																	{formatDate(
																		localOrUtcTimestampToLocalDate(
																			date_local,
																			date
																		),
																		"ddd YYYY"
																	)}
																</small>
															</Box>
														</td>
													) : null
												const hotelCells =
													daywiseServiceIndex === 0 ? (
														<>
															{stayHotels.length ? (
																<>
																	<td rowSpan={rowsCount}></td>
																	<td rowSpan={rowsCount}>
																		{stayHotels.length
																			? stayHotels.map((h, index) => (
																					<Box key={index}>
																						<small>
																							<b>{h.name}</b>
																						</small>
																						<br />
																						{h.hotel ? (
																							<small>
																								{h.hotel.location.short_name}
																							</small>
																						) : null}
																						{h.remarks ? (
																							<small>{h.remarks}</small>
																						) : null}
																					</Box>
																				))
																			: null}
																	</td>
																</>
															) : (
																<>
																	<td rowSpan={rowsCount}>
																		{checkoutHotels.length
																			? checkoutHotels.map((h, index) => (
																					<Box key={index}>
																						<small>
																							<b>{h.name}</b>
																						</small>
																						<br />
																						{h.hotel ? (
																							<small>
																								{h.hotel.location.short_name}
																							</small>
																						) : null}
																						{h.remarks ? (
																							<small>{h.remarks}</small>
																						) : null}
																					</Box>
																				))
																			: null}
																	</td>
																	<td rowSpan={rowsCount}>
																		{checkinHotels.length
																			? checkinHotels.map((h, index) => (
																					<Box key={index}>
																						<small>
																							<b>{h.name}</b>
																						</small>
																						<br />
																						{h.hotel ? (
																							<small>
																								{h.hotel.location.short_name}
																							</small>
																						) : null}
																						{h.remarks ? (
																							<small>{h.remarks}</small>
																						) : null}
																					</Box>
																				))
																			: null}
																	</td>
																</>
															)}
														</>
													) : null
												if (cab_schedule) {
													const {
														cabs,
														start_date,
														start_date_local,
														start_time_local,
														transport_service,
														remarks,
														supplier_remarks,
														itinerary,
														has_cab_time_variation,
														use_hotel_as_pickup,
														use_hotel_as_drop,
														pick_up_location,
														drop_location,
													} = cab_schedule
													const price = getBookingPriceForCabs(cabs)
													totalPrice = addMoneyFromDifferentCurrencies(
														totalPrice.concat(price)
													)

													const normalizedCabs: TConsolidatedCab[] =
														has_cab_time_variation
															? cabs.map((cab) => ({ ...cab, count: 1 }))
															: consolidateCabsByPickup(cabs)
													return (
														<React.Fragment
															key={`cab_schedule_${cab_schedule.id}`}
														>
															{normalizedCabs.map((cab, cabIndex) => {
																const shouldCollapse =
																	hasSameCabsForAll && !has_cab_time_variation

																if (shouldCollapse && cabIndex > 0) return null

																const cabsRows = shouldCollapse
																	? 1
																	: normalizedCabs.length
																return (
																	<tr key={cab.id}>
																		{cabIndex === 0 ? (
																			<>
																				{dateCell}
																				<td
																					rowSpan={cabsRows}
																					colSpan={
																						hasSameCabsForAll
																							? includeHotels
																								? 2
																								: 4
																							: 1
																					}
																				>
																					<Box as="b" whiteSpace="preserve">
																						{transport_service.from_to} -{" "}
																						{
																							transport_service.service_display_name
																						}
																						{includeAllTourist && (
																							<div
																								style={{
																									fontSize: "smaller",
																									color: "#555",
																								}}
																							>
																								<TouristInfoEmailView
																									tourists={
																										cab_schedule.tourists
																									}
																								/>
																							</div>
																						)}
																					</Box>
																					{(remarks || supplier_remarks) &&
																					includeRemarks ? (
																						<p
																							style={{
																								borderLeft: "2px solid silver",
																								paddingLeft: "4px",
																								marginBottom: "4px",
																							}}
																						>
																							<b>Remarks : </b>
																							<Markdown className="remarks">
																								{remarks || supplier_remarks}
																							</Markdown>
																						</p>
																					) : null}
																					{includeItinerary &&
																					itinerary?.description ? (
																						<Markdown className="itinerary">
																							{itinerary.description}
																						</Markdown>
																					) : null}
																				</td>
																			</>
																		) : null}
																		{!hasSameCabsForAll ||
																		daywise_services.some((s) =>
																			s.services.some(
																				(service) =>
																					service.cab_schedule
																						?.has_cab_time_variation
																			)
																		) ? (
																			<>
																				<td
																					colSpan={
																						includePrimaryTourists ? 2 : 1
																					}
																				>
																					{cab.cab ? (
																						<>
																							<b>
																								{cab.count
																									? cab.count + "-"
																									: ""}
																								{cab.cab.name}
																							</b>
																							<br />
																							<small>
																								{cab.cab.number_plate}
																							</small>
																						</>
																					) : (
																						<b>
																							{cab.count ? cab.count + "-" : ""}
																							{cab.cab_type.name}
																						</b>
																					)}
																					{cab.driver && !hasSameCabsForAll ? (
																						<>
																							<br />
																							<b>{cab.driver.name}</b>
																							{cab.driver.phone_number ? (
																								<>
																									<br />
																									<a
																										href={`tel:${cab.driver.phone_number}`}
																									>
																										<small>
																											{cab.driver.phone_number}
																										</small>
																									</a>
																								</>
																							) : null}
																						</>
																					) : null}
																					{(cab.remarks ||
																						cab.supplier_remarks) &&
																					includeRemarks ? (
																						<p
																							style={{
																								borderLeft: "2px solid silver",
																								paddingLeft: "4px",
																							}}
																						>
																							<b>Remarks : </b>
																							<Markdown className="remarks">
																								{cab.remarks ||
																									cab.supplier_remarks}
																							</Markdown>
																						</p>
																					) : null}
																					{includeAllTourist &&
																					cab?.tourists?.length ? (
																						<>
																							<br />
																							<TouristInfoEmailView
																								tourists={cab.tourists}
																							/>
																						</>
																					) : null}
																				</td>
																			</>
																		) : null}
																		{cabIndex === 0 ||
																		has_cab_time_variation ? (
																			<td
																				rowSpan={
																					has_cab_time_variation ? 1 : cabsRows
																				}
																				colSpan={1}
																			>
																				{has_cab_time_variation ? (
																					cab.pickup_time_local ? (
																						<>
																							<b>
																								<small>
																									{formatDate(
																										parseDate(
																											cab.pickup_time_local,
																											"HH:mm:ss"
																										),
																										"HH:mm [hrs]"
																									)}
																								</small>
																							</b>
																							<br />
																						</>
																					) : null
																				) : showPickup(
																						start_date,
																						start_time_local,
																						start_date_local
																				  ) ? (
																					<>
																						<b>
																							<small>
																								{formatDate(
																									start_time_local
																										? parseDate(
																												start_time_local,
																												"HH:mm:ss"
																											)
																										: utcTimestampToLocalDate(
																												start_date
																											),
																									"HH:mm [hrs]"
																								)}
																							</small>
																						</b>
																						<br />
																					</>
																				) : null}
																				{renderWhatsAppLocation({
																					pick_up_location:
																						has_cab_time_variation
																							? cab.pick_up_location
																							: pick_up_location,
																					drop_location: has_cab_time_variation
																						? cab.drop_location
																						: drop_location,
																					use_hotel_as_pickup:
																						has_cab_time_variation
																							? cab.use_hotel_as_pickup
																							: use_hotel_as_pickup,
																					use_hotel_as_drop:
																						has_cab_time_variation
																							? cab.use_hotel_as_drop
																							: use_hotel_as_drop,
																					checkinHotels,
																					checkoutHotels,
																					stayHotels,
																					forEmail: true,
																					startFromNewLine: false,
																				})}
																			</td>
																		) : null}
																		{cabIndex === 0 ? (
																			<>
																				{includePrices ? (
																					<td
																						rowSpan={cabsRows}
																						style={{ textAlign: "right" }}
																						colSpan={includeHotels ? 1 : 2}
																					>
																						{price.length
																							? price
																									.map((p) =>
																										formatMoneyByIntl(p, {
																											showCurrency: true,
																										})
																									)
																									.join(" + ")
																							: null}
																					</td>
																				) : null}
																				{includeHotels ? hotelCells : null}
																			</>
																		) : null}
																	</tr>
																)
															})}
														</React.Fragment>
													)
												}
												if (travel_activity_booking) {
													const {
														start_time_formatted,
														duration_formatted,
														activity,
														ticket_type,
														confirmation_details,
														remarks,
														itinerary,
														booked_price,
														currency,
														ticket_tourist_configurations_full_name_with_complementary,
														pick_up_location,
														drop_location,
														use_hotel_as_pickup,
														use_hotel_as_drop,
													} = travel_activity_booking
													const price = moneyParseByDecimal(
														Number(booked_price || 0),
														currency
													)
													totalPrice = addMoneyFromDifferentCurrencies(
														totalPrice.concat([price])
													)
													return (
														<tr
															key={`travel_activity_booking_${travel_activity_booking.id}`}
														>
															{dateCell}
															<td>
																<Box as="b" whiteSpace="preserve">
																	{activity.name}
																	{ticket_type
																		? ` - ${ticket_type.display_name}`
																		: null}
																	{includeAllTourist && (
																		<div
																			style={{
																				fontSize: "smaller",
																				color: "#555",
																			}}
																		>
																			<TouristInfoEmailView
																				tourists={
																					travel_activity_booking.tourists
																				}
																			/>
																		</div>
																	)}
																</Box>
																{remarks && includeRemarks ? (
																	<>
																		<br />
																		<p
																			style={{
																				fontWeight: "bold",
																				borderLeft: "2px solid silver",
																				paddingLeft: "4px",
																			}}
																		>
																			<small
																				style={{
																					whiteSpace: "pre-line",
																				}}
																			>
																				{remarks}
																			</small>
																		</p>
																	</>
																) : null}
																{includeItinerary && itinerary?.description ? (
																	<Markdown className="itinerary">
																		{itinerary.description}
																	</Markdown>
																) : null}
															</td>
															<td colSpan={includePrimaryTourists ? 2 : 1}>
																<b>
																	{
																		ticket_tourist_configurations_full_name_with_complementary
																	}
																</b>
																{confirmation_details ? (
																	<>
																		<br />
																		<p
																			style={{
																				whiteSpace: "pre-line",
																				display: "inline-block",
																				padding: "2px 4px",
																				backgroundColor: "#f1f3f5",
																			}}
																		>
																			{confirmation_details}
																		</p>
																	</>
																) : null}
															</td>
															<td>
																{start_time_formatted || duration_formatted ? (
																	<>
																		<b>
																			<small>
																				{[
																					start_time_formatted,
																					duration_formatted,
																				]
																					.filter(Boolean)
																					.join(" - ")}
																			</small>
																		</b>
																		<br />
																	</>
																) : null}
																{renderWhatsAppLocation({
																	pick_up_location,
																	drop_location,
																	use_hotel_as_pickup,
																	use_hotel_as_drop,
																	checkinHotels,
																	stayHotels,
																	checkoutHotels,
																	forEmail: true,
																	startFromNewLine: false,
																})}
															</td>
															{includePrices ? (
																<td style={{ textAlign: "right" }}>
																	{Number(booked_price)
																		? formatMoneyByIntl(price, {
																				showCurrency: true,
																			})
																		: null}
																</td>
															) : null}
															{includeHotels ? hotelCells : null}
														</tr>
													)
												}
												return null
											})}
										</React.Fragment>
									)
								})}
							</tbody>
						)
					})}
				</table>
				{includePrices && totalPrice.length ? (
					<>
						<br />
						<Box>
							<b>
								Total Price:{" "}
								{totalPrice
									.map((p) =>
										formatMoneyByIntl(p, {
											showCurrency: true,
											showSolidus: true,
										})
									)
									.join(" + ")}{" "}
							</b>
						</Box>
					</>
				) : null}
			</>
		</EmailShare>
	)
}

function getHasSameCabDriverForSchedules(
	schedules: Array<
		Pick<Required<TTripOperationalBooking>["cab_schedules"][number], "cabs">
	>
) {
	return (
		collect(
			schedules
				.map((s) => {
					const { cabs } = s
					return cabs
						.reduce<[drivers: Array<string>, cabs: Array<string>]>(
							(data, cab) => {
								const drivers = data[0]
								const cabs = data[1]
								const remarksSuffix = cab.remarks ? `:R${cab.remarks}` : ""
								const supplierRemarksSuffix = cab.supplier_remarks
									? `:R${cab.supplier_remarks}`
									: ""
								if (cab.cab) {
									cabs.push(
										`${cab.cab.id}C${remarksSuffix}SR${supplierRemarksSuffix}`
									)
									if (!cab.driver) {
										drivers.push(
											`${cab.cab_type.id}CT${remarksSuffix}SR${supplierRemarksSuffix}`
										)
									}
								}
								if (cab.driver) {
									drivers.push(
										`${cab.driver.id}D${remarksSuffix}SR${supplierRemarksSuffix}`
									)
									if (!cab.cab) {
										drivers.push(
											`${cab.cab_type.id}CT${remarksSuffix}SR${supplierRemarksSuffix}`
										)
									}
								}
								if (!cab.driver && !cab.cab) {
									drivers.push(
										`${cab.cab_type.id}CT${remarksSuffix}SR${supplierRemarksSuffix}`
									)
									cabs.push(
										`${cab.cab_type.id}CT${remarksSuffix}SR${supplierRemarksSuffix}`
									)
								}
								return data
							},
							[[], []]
						)
						.map((d) => d.sort().join(","))
						.filter(Boolean)
						.join("_")
				})
				.filter(Boolean)
		)
			.unique()
			.count() === 1
	)
}

function getBookingPriceForCabs(cabs: Array<IScheduledCab>) {
	return addMoneyFromDifferentCurrencies(
		cabs.map<TMoney>((cab) =>
			moneyParseByDecimal(Number(cab.booked_price || 0), cab.currency)
		)
	)
}

function getWhatsAppContentForHotels(
	{
		stay_hotels,
		checkout_hotels,
		checkin_hotels,
		self_booked_stay_hotels,
		self_booked_checkout_hotels,
		self_booked_checkin_hotels,
	}: Pick<
		TTripOperationalBooking["daywise_services"][number],
		| "stay_hotels"
		| "checkout_hotels"
		| "checkin_hotels"
		| "self_booked_stay_hotels"
		| "self_booked_checkout_hotels"
		| "self_booked_checkin_hotels"
	>,
	hotelsFromPreviousSchedule:
		| Pick<
				TTripOperationalBooking["daywise_services"][number],
				| "stay_hotels"
				| "checkout_hotels"
				| "checkin_hotels"
				| "self_booked_stay_hotels"
				| "self_booked_checkout_hotels"
				| "self_booked_checkin_hotels"
		  >
		| undefined,
	showSelfBooked = false
) {
	const checkinsFromPreviousSchedule = hotelsFromPreviousSchedule
		? hotelsFromPreviousSchedule.checkin_hotels
		: []
	const staysFromPreviousSchedule = hotelsFromPreviousSchedule
		? hotelsFromPreviousSchedule.stay_hotels
		: []
	const previousStaysIds = collect(
		checkinsFromPreviousSchedule
			.concat(staysFromPreviousSchedule)
			.map((b) => b.hotel.id)
			.sort((a, b) => (a > b ? 1 : -1))
	)
		.unique()
		.toArray()
	const currentCheckoutsIds = collect(
		checkout_hotels.map((b) => b.hotel.id).sort((a, b) => (a > b ? 1 : -1))
	)
		.unique()
		.toArray()

	const selfCheckInsForPreviousSchedule = hotelsFromPreviousSchedule
		? hotelsFromPreviousSchedule.self_booked_checkin_hotels
		: []
	const selfStaysForPreviousSchedule = hotelsFromPreviousSchedule
		? hotelsFromPreviousSchedule.self_booked_stay_hotels
		: []
	const selfPreviousStaysIds = collect(
		selfCheckInsForPreviousSchedule
			.concat(selfStaysForPreviousSchedule)
			.map((b) => (b.hotel?.id ? b.hotel.id : b.name))
			.sort((a, b) => (a > b ? 1 : -1))
	)
		.unique()
		.toArray()

	const selfCurrentCheckoutsIds = collect(
		self_booked_checkout_hotels
			.map((b) => (b.hotel?.id ? b.hotel.id : b.name))
			.sort((a, b) => (a > b ? 1 : -1))
	)
		.unique()
		.toArray()

	const areCheckinsFromPreivousSameAsCurrentCheckouts =
		[...previousStaysIds, ...selfPreviousStaysIds].join("-") ===
		[...currentCheckoutsIds, ...selfCurrentCheckoutsIds].join("-")

	const { checkinHotels, stayHotels, checkoutHotels } = combineBookings({
		checkout_hotels,
		checkin_hotels,
		stay_hotels,
		self_booked_checkout_hotels,
		self_booked_checkin_hotels,
		self_booked_stay_hotels,
		showSelfBooked,
	})

	return (
		[
			[
				stayHotels as { hotel?: IHotel; name: string; remarks?: string }[],
				"```Stay    :```",
			],
			[
				// don't render checkouts if we have checkin from previous sames as current checkout
				// And there are we have some checkins/staus for current
				// check fo current checkins/staus is needed to show checkouts on the last schedule
				areCheckinsFromPreivousSameAsCurrentCheckouts &&
				(checkin_hotels.length > 0 ||
					stay_hotels.length > 0 ||
					self_booked_stay_hotels.length > 0 ||
					self_booked_checkin_hotels.length > 0)
					? []
					: checkoutHotels,
				`\`\`\`Checkout:\`\`\``,
			],
			[checkinHotels, `\`\`\`Checkin :\`\`\``],
		] as [{ hotel?: IHotel; name: string; remarks?: string }[], string][]
	)
		.map(([hotels, label]) =>
			!hotels.length
				? ``
				: `${label} ${hotels
						.map((h) => {
							const hotel = h.hotel
								? `*${h.hotel.location.short_name}*
\`\`\`        |\`\`\` *${h.name}*`
								: `*${h.name}*`

							const remarks = h.remarks
								? `\n\`\`\`        |\`\`\` ${h.remarks}`
								: ""
							return hotel + remarks
						})
						.join(`\n\`\`\`        :\`\`\` `)}`
		)
		.filter(Boolean)
		.join("\n")
}

function SelectTripOperationalBookingsToShare({
	tripId,
	onCancel,
	onSelect,
}: {
	tripId: number
	onCancel: () => void
	onSelect: (selectedSchedules: Array<TSharableTripOperationalBooking>) => void
}) {
	const xhr = useXHR()
	const id = useId()
	const { data } = useSWR(
		`/trip-operational-bookings/${tripId}?${id}&share`,
		() =>
			xhr
				.get<{
					data: TTripOperationalBooking
				}>(`/trip-operational-bookings/${tripId}`)
				.then((resp) => resp.data.data)
	)
	const initialValues = useMemo(() => {
		if (!data) {
			return {
				daywise_services: [],
			}
		}
		return {
			daywise_services: data.daywise_services.map((s) => ({
				...s,
				services: s.services.map((s) => ({ ...s, selected: true })),
			})),
		}
	}, [data])
	if (!data) {
		return <Spinner padding="4" alignCenter />
	}
	if (!data.cab_schedules?.length && !data.travel_activity_bookings?.length) {
		return (
			<Box textAlign="center" padding="4">
				No operational bookings for this trip
			</Box>
		)
	}
	return (
		<Stack gap="4">
			<Stack gap="2">
				<Heading as="h4">Select Bookings to Share</Heading>
				<Text color="muted">
					Please select the bookings that you want to share with yours customers
					or service providers.
				</Text>
			</Stack>
			<Form<typeof initialValues>
				initialValues={initialValues}
				onSubmit={async (values) => {
					onSelect([
						{
							...data,
							daywise_services: values.daywise_services
								.map((s) => ({
									...s,
									services: s.services.filter((s) => isTruthy(s.selected)),
								}))
								.filter((s) => s.services.length),
						},
					])
				}}
				subscription={{ submitting: true }}
				mutators={{ ...arrayMutators }}
			>
				{({ handleSubmit, form }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Box>
							<GetFieldValue<
								(typeof initialValues)["daywise_services"]
							> name="daywise_services">
								{({ value: daywise_services }) => (
									<Box
										as="label"
										paddingX="4"
										paddingBottom="2"
										marginBottom="4"
										display="flex"
										alignItems="center"
										borderBottomWidth="2"
										borderColor="default"
										cursor="pointer"
									>
										<CheckboxInput
											name="select_all"
											checked={daywise_services.every((s) =>
												s.services.every((s) => s.selected)
											)}
											onChange={(e) => {
												if (e.currentTarget.checked) {
													// select all the cabs
													form.change(
														"daywise_services",
														daywise_services.map((s) => ({
															...s,
															services: s.services.map((s) => ({
																...s,
																selected: true,
															})),
														}))
													)
												} else {
													// unselect all the cabs
													form.change(
														"daywise_services",
														daywise_services.map((s) => ({
															...s,
															services: s.services.map((s) => ({
																...s,
																selected: false,
															})),
														}))
													)
												}
											}}
										/>
										<Box marginLeft="2" fontWeight="semibold">
											Select All Bookings
										</Box>
									</Box>
								)}
							</GetFieldValue>
							<Box as="ol">
								{initialValues.daywise_services.map(
									({ date, services }, dayIndex) => {
										const daywiseServiceFieldName = `daywise_services[${dayIndex}]`
										return (
											<Inline key={dayIndex} gap="4" as="li" collapseBelow="sm">
												<Box style={{ width: "80px" }}>
													<GetFieldValue<
														(typeof initialValues)["daywise_services"][number]
													>
														name={daywiseServiceFieldName}
													>
														{({ value, onChange }) => (
															<Button
																inline
																title="Select All Bookings of this Day"
																onClick={() => {
																	onChange({
																		...value,
																		services: value.services.map((s) => ({
																			...s,
																			selected: true,
																		})),
																	})
																}}
															>
																<Stack
																	gap="1"
																	padding="1"
																	color="default"
																	textAlign="left"
																>
																	<Text fontSize="md" fontWeight="semibold">
																		{utcTimestampToLocalDateString(
																			date,
																			"D MMM"
																		)}
																	</Text>
																	<Text fontSize="sm">
																		{utcTimestampToLocalDateString(
																			date,
																			"dddd"
																		)}
																	</Text>
																</Stack>
															</Button>
														)}
													</GetFieldValue>
												</Box>
												<Stack flex="1" as="ol">
													{services.map((s, serviceIndex) => {
														const checkboxInputFieldName = `${daywiseServiceFieldName}.services[${serviceIndex}].selected`
														return (
															<Box as="li" key={serviceIndex}>
																<GetFieldValue name={checkboxInputFieldName}>
																	{({ value: selected }) => (
																		<Inline
																			as="label"
																			paddingX="4"
																			paddingY="2"
																			rounded="lg"
																			marginBottom="4"
																			borderWidth="1"
																			backgroundColor={{
																				default: selected
																					? "primary"
																					: "subtle",
																				hover: "primary",
																			}}
																			cursor="pointer"
																			gap="4"
																		>
																			<Box>
																				<CheckboxInputField
																					name={checkboxInputFieldName}
																					type="checkbox"
																				/>
																			</Box>
																			<Box flex="1" minWidth="0">
																				{s.cab_schedule ? (
																					<SelectableCabScheduleItem
																						schedule={s.cab_schedule}
																					/>
																				) : s.travel_activity_booking ? (
																					<SelectableTravelActivityBookingItem
																						schedule={s.travel_activity_booking}
																					/>
																				) : null}
																			</Box>
																		</Inline>
																	)}
																</GetFieldValue>
															</Box>
														)
													})}
												</Stack>
											</Inline>
										)
									}
								)}
							</Box>
						</Box>
						<Box
							textAlign="right"
							marginTop="8"
							borderTopWidth="1"
							paddingTop="8"
						>
							<Inline flexDirection="rowReverse" gap="4">
								<GetFieldValue<
									(typeof initialValues)["daywise_services"]
								> name="daywise_services">
									{({ value: schedules }) => {
										const hasSelectedSomething = schedules.some((s) =>
											s.services.some((s) => s.selected)
										)
										return (
											<Button
												disabled={!hasSelectedSomething}
												status={!hasSelectedSomething ? "warning" : "primary"}
												type="submit"
											>
												{!hasSelectedSomething ? (
													<>
														<Icons.Attention /> Select Some Bookings!
													</>
												) : (
													<>
														Next
														<Icons.ChevronDown rotate="270" />
													</>
												)}
											</Button>
										)
									}}
								</GetFieldValue>
								<Button onClick={onCancel} level="tertiary">
									Cancel
								</Button>
							</Inline>
						</Box>
					</form>
				)}
			</Form>
		</Stack>
	)
}

function SelectableCabScheduleItem({
	schedule,
}: {
	schedule: Required<TTripOperationalBooking>["cab_schedules"][number]
}) {
	return (
		<Stack gap="1">
			<Box display={{ sm: "flex" }} justifyContent="between">
				<Box>
					<Box fontWeight="semibold" display="inlineBlock">
						{schedule.transport_service.from_to}
					</Box>
					<Box display="inline" paddingX="2">
						-
					</Box>
					<Box display="inlineBlock">
						{schedule.transport_service.service_display_name}
					</Box>
				</Box>
			</Box>
			<Box as="ul" fontWeight="normal">
				{schedule.cabs.map((cab) => {
					return (
						<Box
							key={cab.id}
							as="li"
							marginTop="1"
							display={{ sm: "flex" }}
							alignItems="center"
							fontSize="sm"
						>
							<Box
								marginRight="4"
								paddingRight="2"
								borderRightWidth={cab.transport_service_provider ? "4" : "0"}
							>
								{cab.cab_type.display_name}
							</Box>
							{cab.transport_service_provider ? (
								<Box display="flex" flexWrap="wrap">
									{joinAttributes(
										<Box>{cab.transport_service_provider.name}</Box>,
										cab.driver &&
											!cab.transport_service_provider.solo_driver ? (
											<Box>{cab.driver.name}</Box>
										) : null,
										cab.cab ? <Box>{cab.cab.name}</Box> : null,
										cab.cab ? <Box>{cab.cab.number_plate}</Box> : null
									)}
								</Box>
							) : null}
							<Box display="flex">
								{cab.booked ? (
									<Box marginLeft="2">
										<Badge success outlined>
											Booked
										</Badge>
									</Box>
								) : cab.transport_service_provider ? (
									<Box marginLeft="2">
										<Badge outlined warning>
											Assigned
										</Badge>
									</Box>
								) : null}
							</Box>
						</Box>
					)
				})}
			</Box>
		</Stack>
	)
}

function SelectableTravelActivityBookingItem({
	schedule,
}: {
	schedule: Required<TTripOperationalBooking>["travel_activity_bookings"][number]
}) {
	const {
		activity,
		ticket_type,
		ticket_tourist_configurations_short_name,
		is_booked,
		supplier,
	} = schedule
	return (
		<Stack gap="1" fontWeight="normal">
			<Text fontWeight="semibold">
				{activity.name}
				{ticket_type ? ` - ${ticket_type.display_name}` : null}
			</Text>
			<Inline gap="4" fontSize="sm" alignItems="center">
				<Box paddingRight="2" borderRightWidth={supplier ? "4" : "0"}>
					{ticket_tourist_configurations_short_name}
				</Box>
				<Inline gap="2" alignItems="center">
					{supplier ? (
						<Box display="flex" flexWrap="wrap">
							<Box>{supplier.name}</Box>
						</Box>
					) : null}
					<Box display="flex">
						{is_booked ? (
							<Box marginLeft="2">
								<Badge success outlined>
									Booked
								</Badge>
							</Box>
						) : supplier ? (
							<Box marginLeft="2">
								<Badge outlined warning>
									Assigned
								</Badge>
							</Box>
						) : null}
					</Box>
				</Inline>
			</Inline>
		</Stack>
	)
}

function EmailShare({
	children,
	subject,
	disabled,
	headerSection,
}: {
	shareWith: "booking_enquiry" | "transport_service_provider"
	children: React.ReactNode
	subject?: React.ReactNode
	disabled?: boolean
	headerSection?: React.ReactNode
}) {
	return (
		<CopyToClipboard<HTMLDivElement>>
			{({ nodeToCopy, copy }) => (
				<Box position="relative">
					{headerSection ? <Box>{headerSection}</Box> : null}
					{subject ? (
						<Stack gap="4" paddingX="4" paddingTop="4">
							<CopyToClipboard<HTMLDivElement>>
								{({ nodeToCopy, copyNodeTextToClipboard }) => (
									<Stack gap="1">
										<Inline justifyContent="between" alignItems="center">
											<Text fontWeight="semibold">Subject</Text>
											{!disabled ? (
												<CopyToClipboardButton
													size="sm"
													level="primary"
													onClick={async () => {
														return copyNodeTextToClipboard()
													}}
												>
													<Icons.ClipboardCopy /> Copy Subject
												</CopyToClipboardButton>
											) : null}
										</Inline>
										<Box
											paddingY="2"
											paddingX="4"
											borderWidth="1"
											rounded="md"
											bgColor="subtle"
										>
											<Box ref={nodeToCopy}>{subject}</Box>
										</Box>
									</Stack>
								)}
							</CopyToClipboard>
							<Text fontWeight="semibold">Body</Text>
						</Stack>
					) : null}
					{!disabled ? (
						<Box position="sticky" top="0">
							<Box position="absolute" right="0" padding="4">
								<CopyToClipboardButton
									level="primary"
									disabled={disabled}
									onClick={async () => {
										return copy()
									}}
								>
									<Icons.ClipboardCopy /> Copy
								</CopyToClipboardButton>
							</Box>
						</Box>
					) : null}
					<Box padding="4" overflowX="auto">
						<ShadowRoot>
							<Box ref={nodeToCopy}>
								<Box style={{ width: "100%" }}>
									<style>{`* { border: none; margin: 0; }
									td, th, span, p, b, body, li, h1, h2, h3, h4, h5, h6, div, strong, a { font-family: Verdana; font-size: 10pt; }
									.itinerary { max-width: 600px;}
									.itinerary span, .itinerary p, .itinerary b, body, .itinerary li, .itinerary strong, .itinerary a { font-size: smaller; }
									.remarks span, .remarks p, .remarks b, body, .remarks li, .remarks strong, .remarks a { font-size: smaller; }
									table { border-collapse: collapse;}
									td, th { border: 1px solid #dfe3e6; padding: 4px 8px; vertical-align: top; text-align: left;}
									a { color: black; text-decoration: none;}
								`}</style>
									{children}
								</Box>
							</Box>
						</ShadowRoot>
					</Box>
				</Box>
			)}
		</CopyToClipboard>
	)
}

function showPickup(
	pickup_at: string,
	start_time_local: string | undefined | null,
	start_date_local: string | undefined | null
) {
	return Boolean(
		getStartDateTimeOfCabSchedule({
			start_date: pickup_at,
			start_time_local: start_time_local || undefined,
			start_date_local: start_date_local || undefined,
		})
	)
}
